/**
 * main.js — Application controller.
 *
 * Mirrors the Qt MainWindow + PlanSearchWidget exactly:
 *
 *   • Welcome tab = PlanSearchWidget (project number search, file index)
 *   • Each opened PDF gets its own tab with filename as label
 *   • Tabs are closable (× button / Ctrl+W / middle-click)
 *   • Ctrl+Tab / Ctrl+Shift+Tab cycle through tabs
 *   • Tab switching saves/restores the entire viewer + search state
 *   • ALL panels (Metadata, Inspector, Log) HIDDEN by default
 *   • Drag-and-drop accepts .pdf and .bpx files
 */

import * as viewer  from './viewer.js'
import * as overlay from './overlay.js'
import * as search  from './search.js'
import * as state   from './state.js'
import * as api     from './api.js'
import { initAuth, onAuthStateChange, forceLogout } from './auth.js'
import { initChat, openHome, openProjectChat, refreshRecents, setProjectProcessing, setChatContextProvider, setCalloutHandler, addDocCard, addAssistantNotice, openLiveTable, setSavedTablesRefresh } from './chat.js'

// ── DOM refs ──────────────────────────────────────────────────────────────

const fileInput   = document.getElementById('file-input')
const btnPrev     = document.getElementById('btn-prev')
const btnNext     = document.getElementById('btn-next')
const pageLabel   = document.getElementById('page-label')
const pageInput   = document.getElementById('page-input')
const btnZoomOut  = document.getElementById('btn-zoom-out')
const btnZoomIn   = document.getElementById('btn-zoom-in')
const btnZoomFit  = document.getElementById('btn-zoom-fit')
const btnDrawBbox    = document.getElementById('btn-draw-bbox')
const btnTextSelect  = document.getElementById('btn-text-select')
const btnSearch   = document.getElementById('btn-search')
const searchBar   = document.getElementById('search-bar')
const searchInput = document.getElementById('search-input')
const searchGo    = document.getElementById('search-go')
const searchPrevBtn  = document.getElementById('search-prev')
const searchNextBtn  = document.getElementById('search-next')
const searchClose = document.getElementById('search-close')
const searchCount = document.getElementById('search-count')
const welcomeEl   = document.getElementById('welcome')
const scrollContainer = document.getElementById('scroll-container')
const pageWrapper     = document.getElementById('page-wrapper')
const metadataContent = document.getElementById('metadata-content')
const layerText   = document.getElementById('layer-text')
const layerTables = document.getElementById('layer-tables')
const layerPredictions = document.getElementById('layer-predictions')
const logContent  = document.getElementById('log-content')

// Panel toggle buttons
const btnToggleCategories = document.getElementById('btn-toggle-categories')
const btnToggleMetadata  = document.getElementById('btn-toggle-metadata')
const btnToggleInspector = document.getElementById('btn-toggle-inspector')
const btnToggleLog       = document.getElementById('btn-toggle-log')

// Panels
const panelMetadata  = document.getElementById('panel-metadata')
const panelInspector = document.getElementById('panel-inspector')
const panelLog       = document.getElementById('panel-log')

// Classification / organization elements
const classifyBar       = document.getElementById('classify-bar')
const classifyStatus    = document.getElementById('classify-status')
const classifyProgressBar = document.getElementById('classify-progress-bar')
const panelCategories   = document.getElementById('panel-categories')
const categoriesList    = document.getElementById('categories-list')
const calloutsList      = document.getElementById('callouts-list')
const orgViewCategory   = document.getElementById('org-view-category')
const orgViewCallout    = document.getElementById('org-view-callout')
const orgViewDocument   = document.getElementById('org-view-document')
const documentsList     = document.getElementById('documents-list')
const panelResizeHandle = document.getElementById('panel-resize-handle')

// Preferred reading order for construction plan categories
const CATEGORY_ORDER = [
  'TITLE', 'GENERAL SUMMARY', 'SUBSUMMARY', 'NOTES', 'MAPS',
  'TYPICAL SECTIONS', 'PLAN', 'CROSS SECTIONS', 'DETAILS', 'ELEVATION',
  'BRIDGE', 'RIGHT OF WAY', 'GEOTECHNICAL', 'MAINTENANCE OF TRAFFIC',
  'DATA', 'SPECIAL PROVISIONS', 'UNORGANIZED',
]

// PlanSearchWidget elements (kept for compat — DOM elements removed)

viewer.init()

// ── Auth — must complete before the app is used ───────────────────────────
initAuth().then(() => {
  _loadRecentJobs()
  _loadSpecs()
  api.wakeWorker()
  initChat()
  // Report where the user is (active page + view) so the plan agent sees the
  // same context the user does. Pages are 1-based to match the plan tools.
  setChatContextProvider(() => {
    const tab = _tabs.find(t => t.id === _activeTabId)
    // Supporting-doc tabs keep the plan's chat, so fall back to the parent
    // plan id so the agent still answers within the same project.
    const docId = state.getState().docId || tab?.docId || tab?.chatProjectId || null
    if (tab?.type === 'callout') {
      return { docId, page: null, view: `callout:${tab.callout?.text || ''}` }
    }
    // Tell the agent exactly which document the user is looking at right now so
    // it can answer about the plan vs. a proposal/addendum/spec accordingly.
    if (tab?.viewOnly && tab?.viewDoc) {
      return {
        docId,
        page: viewer.currentPage() + 1,
        view: `viewing ${tab.viewDoc.kind}: ${tab.viewDoc.filename}`,
        viewing: tab.viewDoc,
      }
    }
    return { docId, page: viewer.currentPage() + 1, view: 'viewing the plan' }
  })
  // Let the chat hyperlink known callouts in assistant replies and open their
  // contact-sheet tab, using the SAME data + action the callout panel uses.
  // Page references ("page 10") navigate the MAIN viewer, not a separate tab.
  setCalloutHandler(() => _callouts || [], (c) => _openCalloutTab(c), (ref) => _goToCitedPage(ref))
  // A live table saved from chat lands in the SAME store as Pull-Table exports;
  // refresh the bottom-left panel so it shows up immediately.
  setSavedTablesRefresh((docId) => _loadSavedTables(docId, { expand: true }))
  // A chat chip and its plan are one unit — the chip dispatches this so the
  // same unified selection runs (open plan tab + load chat on home).
  window.addEventListener('planview:select-project', (e) => {
    const { docId, filename } = e.detail || {}
    selectProject(docId, filename, true)   // came from a chat chip -> reveal the conversation
  })
  // Clerk fires this listener on EVERY session-token refresh (~once a minute),
  // not just on real sign-in/out. Only react to an actual state TRANSITION —
  // otherwise we'd POST /worker/wake every minute, which keeps the GPU worker
  // pinned "up" forever (each wake refreshes its idle timer) and bills all day.
  let _prevSignedIn = true
  onAuthStateChange((signedIn) => {
    if (signedIn === _prevSignedIn) return
    _prevSignedIn = signedIn
    if (signedIn) { _loadRecentJobs(); _loadSpecs(); api.wakeWorker() }
    else {
      _resetWorkspaceForSignOut()
      _clearRecentJobs()
      _clearSpecs()
    }
  })
})

// ── Panel toggle logic (ALL hidden by default — matches Qt) ───────────────

function togglePanel(panelEl, btnEl) {
  const hidden = panelEl.classList.toggle('hidden')
  btnEl.classList.toggle('active', !hidden)
}

// Auto-hiding ribbon — Pin toggle keeps the toolbar/tabs visible
const ribbonEl = document.getElementById('ribbon')
const btnPinRibbon = document.getElementById('btn-pin-ribbon')
btnPinRibbon?.addEventListener('click', () => {
  const pinned = ribbonEl?.classList.toggle('pinned')
  btnPinRibbon.classList.toggle('active', !!pinned)
})

// Clicking into the ribbon focuses a control, and CSS :focus-within would keep
// it down indefinitely. Collapse it ~5s after the last interaction by dropping
// focus (unless pinned or the cursor is still hovering it).
const RIBBON_IDLE_MS = 5000
let _ribbonHideTimer = null
let _ribbonHovered = false
function _scheduleRibbonCollapse() {
  clearTimeout(_ribbonHideTimer)
  if (ribbonEl?.classList.contains('pinned')) return
  _ribbonHideTimer = setTimeout(() => {
    if (ribbonEl?.classList.contains('pinned') || _ribbonHovered) return
    const active = document.activeElement
    if (active && ribbonEl?.contains(active)) active.blur()
  }, RIBBON_IDLE_MS)
}
ribbonEl?.addEventListener('mouseenter', () => { _ribbonHovered = true; clearTimeout(_ribbonHideTimer) })
ribbonEl?.addEventListener('mouseleave', () => { _ribbonHovered = false; _scheduleRibbonCollapse() })
ribbonEl?.addEventListener('focusin', _scheduleRibbonCollapse)
ribbonEl?.addEventListener('keydown', _scheduleRibbonCollapse)
ribbonEl?.addEventListener('click', _scheduleRibbonCollapse)

// Sign-out lives on the welcome screen (the ribbon's Clerk button is hidden there).
document.getElementById('welcome-signout')?.addEventListener('click', () => forceLogout())

// Fullscreen toggle — reclaims the browser's tab/address-bar chrome for maximum
// viewer height. The window 'resize' that fullscreen fires makes the viewer re-fit
// automatically (viewer._onResize), so the plan grows to fill the taller area.
const btnFullscreen = document.getElementById('btn-fullscreen')
btnFullscreen?.addEventListener('click', async () => {
  try {
    if (document.fullscreenElement) {
      await document.exitFullscreen()
    } else {
      await document.documentElement.requestFullscreen()
    }
  } catch (err) {
    log(`Fullscreen unavailable: ${err?.message ?? err}`)
  }
})
document.addEventListener('fullscreenchange', () => {
  const on = !!document.fullscreenElement
  btnFullscreen?.classList.toggle('active', on)
  if (btnFullscreen) btnFullscreen.title = on
    ? 'Exit full screen (F11 / Esc)'
    : 'Full screen (F11) — hide browser chrome for more height'
})

btnToggleCategories.addEventListener('click', () => togglePanel(panelCategories, btnToggleCategories))
btnToggleMetadata.addEventListener('click', () => togglePanel(panelMetadata, btnToggleMetadata))
btnToggleInspector.addEventListener('click', () => togglePanel(panelInspector, btnToggleInspector))
btnToggleLog.addEventListener('click', () => togglePanel(panelLog, btnToggleLog))

// Organization view toggle: By Category / By Callout
orgViewCategory?.addEventListener('click', () => { _orgView = 'category'; _renderOrgView() })
orgViewCallout?.addEventListener('click', () => { _orgView = 'callout'; _renderOrgView() })
orgViewDocument?.addEventListener('click', () => { _orgView = 'document'; _renderOrgView() })

// Panel close buttons
document.querySelectorAll('.panel-close').forEach(btn => {
  btn.addEventListener('click', () => {
    const panelName = btn.dataset.panel
    if (panelName === 'metadata')  { panelMetadata.classList.add('hidden'); btnToggleMetadata.classList.remove('active') }
    if (panelName === 'inspector') { panelInspector.classList.add('hidden'); btnToggleInspector.classList.remove('active') }
    if (panelName === 'log')       { panelLog.classList.add('hidden'); btnToggleLog.classList.remove('active') }
    if (panelName === 'categories') { panelCategories.classList.add('hidden'); btnToggleCategories.classList.remove('active') }
  })
})

// ── Tab system (matches Qt QTabWidget) ────────────────────────────────────

/**
 * @typedef {Object} Tab
 * @property {string}  id
 * @property {string}  label
 * @property {'welcome'|'document'|'callout'} type
 * @property {File|null}  file
 * @property {string|null} docId
 * @property {object|null} viewerState
 * @property {object|null} appState
 * @property {Map|null}    pages
 */

const docTabsList = document.getElementById('doc-tabs-list')

/** @type {Tab[]} */
let _tabs = []
/** @type {string} */
let _activeTabId = ''
const _openingCachedDocIds = new Set()
// Doc ids deleted this session. The server's recent-list can briefly still
// return a just-deleted doc (Spaces manifest overwrite lag across the two
// uvicorn workers), which would re-add the row after we optimistically
// removed it. Filtering on render guarantees a deleted plan never reappears.
const _deletedDocIds = new Set()
let _viewerPrepOverlay = null
let _viewerPerfTimer = null
let _viewerPerfDocId = null
let _viewerPerfLastLine = ''

let _tabIdCounter = 0
function _nextTabId() { return `tab-${++_tabIdCounter}` }

function _tabLabelFromFilename(filename) {
  return (filename || 'Untitled').replace(/\.(pdf|bpx)$/i, '')
}

function _findOpenDocumentTabId(docId) {
  if (!docId) return null
  const tab = _tabs.find(t => t.type === 'document' && t.docId === docId)
  return tab?.id || null
}

function _destroyPdfProxy(pdf, seen) {
  if (!pdf || seen.has(pdf)) return
  seen.add(pdf)
  try {
    const maybePromise = pdf.destroy?.()
    if (maybePromise && typeof maybePromise.catch === 'function') {
      maybePromise.catch(() => {})
    }
  } catch { /* ignore */ }
}

function _updateOpenTabsForRenamedDocument(docId, filename) {
  if (!docId || !filename) return
  let changed = false
  for (const tab of _tabs) {
    if (tab.docId !== docId) continue
    if (tab.type === 'document') {
      tab.label = _tabLabelFromFilename(filename)
    }
    if (tab.appState) tab.appState.fileName = filename
    if ('fileName' in tab) tab.fileName = filename
    changed = true
  }
  if (state.getState().docId === docId) {
    _fileName = filename
    _updateMetadata(filename, _pdf?.numPages ?? state.getState().pageCount ?? 0)
  }
  if (changed) _renderTabBar()
}

function _closeTabsForDocument(docId) {
  if (!docId) return
  const ids = _tabs.filter(tab => tab.docId === docId).map(tab => tab.id)
  for (const id of ids) _closeTab(id)
}

function _resetWorkspaceForSignOut() {
  clearTimeout(_recentJobsTimer)
  _recentJobsTimer = null

  viewer.detach()

  const seen = new Set()
  _destroyPdfProxy(_pdf, seen)
  for (const tab of _tabs) {
    _destroyPdfProxy(tab.viewerState?.pdf, seen)
    _destroyPdfProxy(tab.pdf, seen)
  }

  state.resetDocument()

  _pdf = null
  _fileName = ''
  _searchResults = []
  _searchFlat = []
  _searchFlatIdx = -1
  _classifyResults = null
  _callouts = null
  _calloutHighlightMap = null
  _tabs = []
  _activeTabId = ''

  panelCategories.classList.add('hidden')
  panelMetadata.classList.add('hidden')
  panelInspector.classList.add('hidden')
  panelLog.classList.add('hidden')
  btnToggleCategories.classList.remove('active')
  btnToggleMetadata.classList.remove('active')
  btnToggleInspector.classList.remove('active')
  btnToggleLog.classList.remove('active')
  _showCalloutView(false)
  _clearPageStrip()
  _clearSearch()
  _createWelcomeTab()
}

// ── Create the Welcome tab (always first, unclosable) ─────────────────────

function _createWelcomeTab() {
  const id = _nextTabId()
  /** @type {Tab} */
  const tab = {
    id,
    label: 'Welcome',
    type: 'welcome',
    file: null,
    docId: null,
    viewerState: null,
    appState: null,
    pages: null,
  }
  _tabs.push(tab)
  _renderTabBar()
  _activateTab(id)
}

// ── Per-tab local state ───────────────────────────────────────────────────

/** @type {import('pdfjs-dist').PDFDocumentProxy|null} */
let _pdf = null
let _fileName = ''
let _searchResults = []
let _searchFlatIdx = -1
/** @type {{ page: number, rect: object }[]} */
let _searchFlat    = []

/** Classification results for the current tab (null if not classified) */
let _classifyResults = null

/** Table-callout groups for the current tab (null if not preprocessed) */
let _callouts = null

/** Per-page callout highlight rects (PDF-point space) for a callout tab */
let _calloutHighlightMap = null

/** Left-panel organization view: 'category' | 'callout' (global preference) */
let _orgView = 'category'

function _saveAppState() {
  return {
    pdf:              _pdf,
    fileName:         _fileName,
    searchResults:    _searchResults,
    searchFlat:       _searchFlat,
    searchFlatIdx:    _searchFlatIdx,
    classifyResults:  _classifyResults,
    callouts:         _callouts,
    calloutHighlightMap: _calloutHighlightMap,
    pageFilter:       viewer.getPageFilter(),
  }
}

function _restoreAppState(s) {
  _pdf            = s.pdf
  _fileName       = s.fileName
  _searchResults  = s.searchResults  || []
  _searchFlat     = s.searchFlat     || []
  _searchFlatIdx  = s.searchFlatIdx  ?? -1
  _classifyResults = s.classifyResults || null
  _callouts       = s.callouts || null
  _calloutHighlightMap = s.calloutHighlightMap || null
  viewer.setPageFilter(s.pageFilter || null)
}

// ── Tab bar rendering ─────────────────────────────────────────────────────

function _renderTabBar() {
  docTabsList.innerHTML = ''
  for (const tab of _tabs) {
    const el = document.createElement('button')
    el.className = 'doc-tab' + (tab.id === _activeTabId ? ' active' : '')
    el.dataset.tabId = tab.id

    const labelSpan = document.createElement('span')
    labelSpan.className = 'doc-tab-label'
    labelSpan.textContent = tab.label
    labelSpan.title = tab.label
    el.appendChild(labelSpan)

    // Close button (not for Welcome tab)
    if (tab.type !== 'welcome') {
      const closeBtn = document.createElement('button')
      closeBtn.className = 'doc-tab-close'
      closeBtn.textContent = '\u00d7'
      closeBtn.title = 'Close tab (Ctrl+W)'
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation()
        _closeTab(tab.id)
      })
      el.appendChild(closeBtn)
    }

    el.addEventListener('click', () => _switchToTab(tab.id))

    // Middle-click to close
    el.addEventListener('auxclick', (e) => {
      if (e.button === 1 && tab.type !== 'welcome') {
        e.preventDefault()
        _closeTab(tab.id)
      }
    })

    docTabsList.appendChild(el)
  }
}

// ── Tab activation ────────────────────────────────────────────────────────

function _activateTab(tabId) {
  _activeTabId = tabId
  const tab = _tabs.find(t => t.id === tabId)
  if (!tab) return
  // Chat overlay switches to transparent, click-through mode over an open plan.
  document.body.classList.toggle('doc-active', tab.type !== 'welcome')

  if (tab.type === 'welcome') {
    _stopViewerPerfPolling()
    // Show welcome (PlanSearchWidget), hide viewer
    viewer.detach()
    _showCalloutView(false)
    welcomeEl.classList.add('active')
    openHome()   // welcome always shows the persistent home assistant
    scrollContainer.classList.add('hidden')
    searchBar.style.display = 'none'
    _setDrawMode(false)
    _setTextSelectMode(false)
    _pdf = null
    _fileName = ''
    _clearSearch()
    _updatePageLabel()
    _updateNavButtons()
    _updateMetadata(null, 0)
    btnDrawBbox.disabled = true
    btnTextSelect.disabled = true
    panelCategories.classList.add('hidden')
    _loadSavedTables(null)
    _classifyResults = null
    _callouts = null
    _calloutHighlightMap = null
    viewer.setPageFilter(null)
    _clearPageStrip()
    // welcome screen — nothing to focus
  } else if (tab.type === 'callout') {
    _stopViewerPerfPolling()
    // Contact-sheet view of all pages referencing a callout
    viewer.detach()
    welcomeEl.classList.remove('active')
    scrollContainer.classList.add('hidden')
    searchBar.style.display = 'none'
    _setDrawMode(false)
    _setTextSelectMode(false)
    btnDrawBbox.disabled = true
    btnTextSelect.disabled = true
    panelCategories.classList.add('hidden')
    _clearPageStrip()
    pageStrip.classList.add('hidden')
    _pdf = tab.pdf
    _fileName = tab.fileName
    _showCalloutView(true)
    _renderCalloutView(tab.callout, tab.calloutZoom || 1.0, tab.docId, tab.renderedManifest)
    _updateMetadata(tab.fileName, tab.pdf?.numPages ?? 0)
  } else {
    let viewerReady = Promise.resolve()
    if (tab.docId) _startViewerPerfPolling(tab.docId)
    else _stopViewerPerfPolling()
    // ONE chat for the whole project: the plan tab and every supporting-doc tab
    // opened from it bind to the SAME project chat (via chatProjectId).
    const chatId = tab.docId || tab.chatProjectId
    if (chatId) openProjectChat(chatId, tab.docId ? tab.label : (tab.chatProjectLabel || tab.label), false)
    if (tab.docId) _loadSavedTables(tab.docId)
    // Show viewer, hide welcome
    _showCalloutView(false)
    welcomeEl.classList.remove('active')
    scrollContainer.classList.remove('hidden')
    searchBar.style.display = 'flex'

    if (tab.appState) _restoreAppState(tab.appState)
    if (tab.pages) {
      state.resetDocument()
      const ps = tab.pages
      if (ps) {
        for (const [k, v] of ps.entries()) {
          const pageState = state.getPageState(k)
          pageState.artifacts  = v.artifacts
          pageState.annotations = v.annotations
        }
      }
      state.update({ docId: tab.docId ?? null, pageCount: tab.viewerState?.pdf?.numPages ?? 0, currentPage: tab.viewerState?.currentPageIdx ?? 0 })
    }

    if (tab.viewerState) {
      viewerReady = viewer.restoreState(tab.viewerState).then(() => {
        // Supporting docs are read-only: keep the bbox tool muted so it can't be clicked.
        btnDrawBbox.disabled = !!tab.viewOnly
        // Text-select works in both modes now: pdf.js pages have native text, and
        // rendered-mode pages get a lazy vector text overlay (Feature A).
        btnTextSelect.disabled = false
        _updatePageLabel()
        _updateNavButtons()
        _refreshOverlayData()
        _buildPageStrip(_pdf)
      })
    }
    _updateMetadata(_fileName, tab.viewerState?.pdf?.numPages ?? 0)

    // Restore classification / category panel state. Supporting-doc tabs are
    // view-only and never carry plan classification, so hide it outright.
    if (tab.viewOnly) {
      panelCategories.classList.add('hidden')
    } else if (_classifyResults) {
      _renderOrgView()
      panelCategories.classList.remove('hidden')
    } else {
      panelCategories.classList.add('hidden')
    }
    _renderTabBar()
    return viewerReady
  }

  _renderTabBar()
}

function _switchToTab(tabId) {
  if (tabId === _activeTabId) return Promise.resolve()
  _saveCurrentTab()
  return _activateTab(tabId)
}

function _saveCurrentTab() {
  const tab = _tabs.find(t => t.id === _activeTabId)
  if (!tab) return

  if (tab.type === 'document') {
    tab.viewerState = viewer.saveState()
    tab.appState    = _saveAppState()
    tab.pages = new Map()
    const s = state.getState()
    for (const [k, v] of s.pages.entries()) {
      tab.pages.set(k, { artifacts: v.artifacts, annotations: [...v.annotations] })
    }
  }
}

// ── Close tab ─────────────────────────────────────────────────────────────

function _closeTab(tabId) {
  const idx = _tabs.findIndex(t => t.id === tabId)
  if (idx === -1) return
  const tab = _tabs[idx]
  if (tab.type === 'welcome') return

  const pdfToDestroy = tab.viewerState?.pdf
  if (pdfToDestroy && tabId !== _activeTabId && !tab.sharedPdf) {
    pdfToDestroy.destroy().catch(() => {})
  }

  _tabs.splice(idx, 1)
  log(`Closed: ${tab.label}`)

  if (tabId === _activeTabId) {
    const nextIdx = Math.min(idx, _tabs.length - 1)
    _activeTabId = ''
    _activateTab(_tabs[nextIdx].id)
  } else {
    _renderTabBar()
  }
}

// ── Tab cycling ───────────────────────────────────────────────────────────

function _cycleTab(delta) {
  if (_tabs.length <= 1) return
  const curIdx = _tabs.findIndex(t => t.id === _activeTabId)
  const next   = (curIdx + delta + _tabs.length) % _tabs.length
  _switchToTab(_tabs[next].id)
}

// ── Log helper (matches Qt LogPanel — [HH:MM:SS] message) ─────────────────

function log(msg) {
  const ts = new Date().toLocaleTimeString('en-GB', { hour12: false })
  logContent.textContent += `[${ts}] ${msg}\n`
  logContent.scrollTop = logContent.scrollHeight
}

function _startViewerPerfPolling(docId) {
  if (!docId) return _stopViewerPerfPolling()
  if (_viewerPerfDocId === docId && _viewerPerfTimer) return
  _stopViewerPerfPolling()
  _viewerPerfDocId = docId

  const poll = async () => {
    if (_viewerPerfDocId !== docId) return
    try {
      const status = await api.getDocumentViewerStatus(docId)
      const thumbs = status.thumb_progress || { done: 0, total: 0 }
      const previews = status.preview_progress || { done: 0, total: 0 }
      const tiles = status.tile_progress || { done: 0, total: 0 }
      const last = status.last_render
        ? `, last ${status.last_render.kind} p${status.last_render.page}: ${Math.round(status.last_render.total_ms || 0)}ms`
        : ''
      const active = status.active_render_count || 0
      const queued = status.queued_priority_requests || 0
      const line = `Viewer cache: previews ${previews.done}/${previews.total}, thumbs ${thumbs.done}/${thumbs.total}, tiles ${tiles.done}, active ${active}, queued ${queued}${last}`
      if (line !== _viewerPerfLastLine) {
        _viewerPerfLastLine = line
        log(line)
      }
    } catch (err) {
      const line = `Viewer cache status unavailable: ${err.message}`
      if (line !== _viewerPerfLastLine) {
        _viewerPerfLastLine = line
        log(line)
      }
    } finally {
      if (_viewerPerfDocId === docId) {
        _viewerPerfTimer = setTimeout(poll, 5000)
      }
    }
  }
  poll()
}

function _stopViewerPerfPolling() {
  clearTimeout(_viewerPerfTimer)
  _viewerPerfTimer = null
  _viewerPerfDocId = null
  _viewerPerfLastLine = ''
}

// ── viewer.js event wiring ────────────────────────────────────────────────

viewer.on('prerender', () => {
  overlay.hide()
})

viewer.on('pagerender', (e) => {
  overlay.onPageRender(e.detail.canvas, e.detail.scale)
  _refreshOverlayData()
})

viewer.on('scalechange', (e) => {
  _updatePageLabel()
})

viewer.on('zoommodechange', (e) => {
  _updatePageLabel()
  // Zoom mode uses left-drag for pan — mutually exclusive with draw mode.
  // Save draw state on zoom-enter; restore it on zoom-exit.
  if (e.detail?.active) {
    _drawModeBeforeZoom = _drawMode
    _setDrawMode(false)
  } else {
    if (_drawModeBeforeZoom) _setDrawMode(true)
    _drawModeBeforeZoom = false
  }
})

viewer.on('navnext', () => navigate(+1))
viewer.on('navprev', () => navigate(-1))

// ── Navigation (matches Qt page nav exactly) ──────────────────────────────

function navigate(delta) {
  const s = state.getState()
  if (!s.pageCount) return

  const filter = viewer.getPageFilter()
  let next

  if (filter && filter.length > 0) {
    const curIdx = filter.indexOf(s.currentPage)
    let nextIdx
    if (curIdx === -1) {
      nextIdx = delta > 0 ? 0 : filter.length - 1
    } else {
      nextIdx = Math.max(0, Math.min(filter.length - 1, curIdx + delta))
      if (nextIdx === curIdx) return
    }
    next = filter[nextIdx]
  } else {
    next = Math.max(0, Math.min(s.pageCount - 1, s.currentPage + delta))
    if (next === s.currentPage) return
  }

  state.update({ currentPage: next })
  viewer.goToPage(next)
  _updatePageLabel()
  _updateNavButtons()
  _loadPageData(next)
  _refreshSearchHighlightsForPage(next)
  _updateActiveStripThumb(next)
}

function _updatePageLabel() {
  const s = state.getState()
  if (!s.pageCount) { pageLabel.textContent = '\u2014'; return }

  const filter = viewer.getPageFilter()
  let label

  if (filter && filter.length > 0) {
    const filterIdx = filter.indexOf(s.currentPage)
    label = filterIdx >= 0
      ? `Page ${filterIdx + 1} / ${filter.length} (Sheet ${s.currentPage + 1})`
      : `Sheet ${s.currentPage + 1} / ${s.pageCount}`
  } else {
    label = `Page ${s.currentPage + 1} / ${s.pageCount}`
  }

  if (viewer.isZoomMode()) {
    const pct = Math.round(viewer.getUserZoom() * 100)
    pageLabel.textContent = `\u{1F50D} ${pct}% \u2014 ${label}`
  } else {
    pageLabel.textContent = label
  }

  if (document.activeElement !== pageInput) {
    pageInput.value = String(s.currentPage + 1)
  }
}

function _updateNavButtons() {
  const s = state.getState()
  const filter = viewer.getPageFilter()
  if (filter && filter.length > 0) {
    const filterIdx = filter.indexOf(s.currentPage)
    btnPrev.disabled = filterIdx <= 0
    btnNext.disabled = filterIdx >= filter.length - 1
  } else {
    btnPrev.disabled = s.currentPage <= 0
    btnNext.disabled = s.currentPage >= s.pageCount - 1
  }
}

btnPrev.addEventListener('click', () => navigate(-1))
btnNext.addEventListener('click', () => navigate(+1))

// Jump to page
pageInput.addEventListener('keydown', (e) => {
  if (e.key !== 'Enter') return
  e.preventDefault()
  const idx = parseInt(pageInput.value, 10) - 1
  const s = state.getState()
  if (!isNaN(idx) && idx >= 0 && idx < s.pageCount) {
    state.update({ currentPage: idx })
    viewer.goToPage(idx)
    _updatePageLabel()
    _updateNavButtons()
    _loadPageData(idx)
    _refreshSearchHighlightsForPage(idx)
    _updateActiveStripThumb(idx)
  }
})

// ── Zoom toolbar ──────────────────────────────────────────────────────────

btnZoomOut.addEventListener('click', () => viewer.zoomOut())
btnZoomIn.addEventListener('click',  () => viewer.zoomIn())
btnZoomFit.addEventListener('click', () => viewer.zoomFit())

// ── Open PDF — creates a new tab for each file ────────────────────────────

fileInput.addEventListener('change', async () => {
  const files = fileInput.files
  if (!files?.length) return
  fileInput.value = ''
  for (const file of files) {
    await _ingestFile(file)
  }
})

// ── Smart ingest: drop/browse a file → the vision model says plan or spec →
// route it (plan → preprocess, spec → library). No document auto-opens a tab;
// it shows in the chat (page-1 card) and in the left menu, clickable. ────────

async function _ingestFile(file) {
  const ln = file.name.toLowerCase()
  if (ln.endsWith('.bpx')) {
    // Bluebeam packages are plan sets — keep the existing extract/open flow.
    await _openBpxFile(file)
    return
  }
  if (!ln.endsWith('.pdf')) { log(`Unsupported file type: ${file.name}`); return }

  await openHome()   // land the card in the welcome/home chat feed (await so
                     // history loads FIRST -- otherwise the card is shoved above it)
  const card = addDocCard({ filename: file.name, status: 'Reading…' })

  // Render the first page for the card + the type prompt.
  let thumb = ''
  try {
    thumb = await viewer.renderFileFirstPageThumb(file)
    card?.setThumb(thumb)
  } catch (e) {
    console.warn('first-page render failed', e)
  }

  // Ask the user directly — plan, proposal/addendum, or spec.
  card?.setStatus('What kind of document is this?')
  const type = await _askDocType(file.name, thumb)
  if (!type) { card?.setStatus('Cancelled'); return }

  if (type === 'spec') await _ingestSpec(file, card)
  else if (type === 'proposal' || type === 'addendum') await _attachToPlanFromHome(file, type, card)
  else await _ingestPlan(file, card)
}

async function _ingestPlan(file, card) {
  addAssistantNotice('I\u2019m processing your plan \u2014 this can take a minute. I\u2019ll let you know right here when it\u2019s ready.')
  card?.setStatus('Uploading plan… 0%')
  try {
    const res = await api.uploadDocument(file, {
      onProgress: (e) => {
        if (e && e.lengthComputable) {
          card?.setStatus(`Uploading plan… ${Math.round((e.loaded / e.total) * 100)}%`)
        }
      },
    })
    let docId = res.doc_id
    let planName = file.name

    // Bluebeam package? Do the normal check and let the user pick the plan PDF
    // inside it BEFORE preprocessing — never preprocess the wrapper itself.
    card?.setStatus('Checking for embedded documents…')
    let check = null
    try { check = await api.checkDocumentBluebeam(docId) } catch { /* not fatal */ }
    if (check && check.is_bluebeam && check.embedded?.length > 0) {
      const chosen = await _chooseEmbedded(check.embedded)
      if (!chosen) { card?.setStatus('Cancelled — pick a PDF to preprocess'); return }
      card?.setStatus('Extracting embedded PDF…')
      const emb = await api.extractDocumentBluebeamToDocument(docId, chosen.index)
      docId = emb.doc_id
      planName = emb.filename || chosen.name || planName
    }

    _refreshRecentJobsSoon()                       // shows in the left Recent Jobs menu
    card?.setStatus('Preprocessing…')
    card?.setClickable(() => selectProject(docId, planName))
    api.preprocessDocument(docId, {
      onStatus: (s) => { if (s?.message) card?.setStatus(s.message) },
      onProgress: (p) => card?.setStatus(p.total ? `Preprocessing ${p.done}/${p.total}…` : 'Preprocessing…'),
    }).then(() => {
      card?.setStatus('Plan ready — click to open')
      api.markDocumentReady(docId)
      _refreshRecentJobsSoon()
    }).catch((err) => {
      card?.setStatus(`Preprocess failed: ${err.message}`)
    })
  } catch (err) {
    card?.setStatus(`Upload failed: ${err.message}`)
    log(`Plan upload failed: ${err.message}`)
  }
}

async function _ingestSpec(file, card) {
  card?.setStatus('Saving to your specifications…')
  try {
    await api.uploadSpec(file)
    await _loadSpecs()
    card?.setStatus('Saved to your specifications')
  } catch (err) {
    card?.setStatus(`Spec save failed: ${err.message}`)
    log(`Spec save failed: ${err.message}`)
  }
}

/** Home-screen path: a proposal/addendum can't start a project -- it must
 *  attach to an existing plan, so let the user pick which one. */
async function _attachToPlanFromHome(file, kind, card) {
  card?.setStatus('Loading your plans…')
  let plans = []
  try { plans = await api.listRecentJobs(36500, 500) } catch {}
  plans = (plans || []).filter(p => p.doc_id && !_deletedDocIds.has(p.doc_id))
  if (!plans.length) {
    card?.setStatus('No plan to attach to — upload a plan first')
    return
  }
  const chosen = await _pickPlan(file.name, kind, plans)
  if (!chosen) { card?.setStatus('Cancelled'); return }
  card?.setStatus(`Attaching ${kind} to ${chosen.filename || 'plan'}…`)
  try {
    await api.addProjectAttachment(chosen.doc_id, file, kind)
    card?.setStatus(`Attached ${kind} to ${chosen.filename || 'plan'}`)
    card?.setClickable(() => selectProject(chosen.doc_id, chosen.filename))
  } catch (e) {
    card?.setStatus(`Attach failed: ${e.message}`)
  }
}

/** Choose which plan (project) a proposal/addendum attaches to. */
function _pickPlan(filename, kind, plans) {
  return new Promise((resolve) => {
    const back = document.createElement('div')
    back.className = 'modal-overlay'
    back.innerHTML = `
      <div class="modal-box doctype-modal">
        <h3>Attach ${kind} to which plan?</h3>
        <p class="doctype-file">${filename}</p>
        <div class="embed-list"></div>
        <div class="doctype-actions">
          <button class="modal-btn" data-cancel="1">Cancel</button>
        </div>
      </div>`
    const list = back.querySelector('.embed-list')
    const done = (val) => { try { document.body.removeChild(back) } catch {} ; resolve(val) }
    for (const p of plans) {
      const b = document.createElement('button')
      b.className = 'modal-btn modal-btn-primary embed-item'
      b.textContent = p.filename || p.doc_id
      b.title = p.filename || ''
      b.addEventListener('click', () => done(p))
      list.appendChild(b)
    }
    back.addEventListener('click', (e) => {
      if (e.target === back || e.target.closest('button[data-cancel]')) done(null)
    })
    document.body.appendChild(back)
  })
}

/** Ask which embedded PDF (from a Bluebeam package) to use. Resolves {index, name} or null. */
function _chooseEmbedded(embedded) {
  return new Promise((resolve) => {
    const back = document.createElement('div')
    back.className = 'modal-overlay'
    back.innerHTML = `
      <div class="modal-box doctype-modal">
        <h3>Bluebeam package</h3>
        <p class="doctype-file">Choose the plan PDF inside to preprocess:</p>
        <div class="embed-list"></div>
        <div class="doctype-actions">
          <button class="modal-btn" data-cancel="1">Cancel</button>
        </div>
      </div>`
    const list = back.querySelector('.embed-list')
    const done = (val) => { try { document.body.removeChild(back) } catch {} ; resolve(val) }
    for (const e of embedded) {
      const b = document.createElement('button')
      b.className = 'modal-btn modal-btn-primary embed-item'
      b.textContent = e.name || `Embedded PDF ${e.index}`
      b.title = e.name || ''
      b.addEventListener('click', () => done(e))
      list.appendChild(b)
    }
    back.addEventListener('click', (e) => {
      if (e.target === back || e.target.closest('button[data-cancel]')) done(null)
    })
    document.body.appendChild(back)
  })
}

/** Ask the user whether a document is a plan or a spec. Resolves 'plan' | 'spec' | null. */
function _askDocType(filename, thumb) {
  return new Promise((resolve) => {
    const back = document.createElement('div')
    back.className = 'modal-overlay'
    back.innerHTML = `
      <div class="modal-box doctype-modal">
        <h3>What kind of document is this?</h3>
        <p class="doctype-file"></p>
        ${thumb ? '<img class="doctype-thumb" alt="">' : ''}
        <div class="doctype-actions">
          <button class="modal-btn modal-btn-primary" data-t="plan">Plan</button>
          <button class="modal-btn modal-btn-primary" data-t="proposal">Proposal</button>
          <button class="modal-btn modal-btn-primary" data-t="addendum">Addendum</button>
          <button class="modal-btn modal-btn-primary" data-t="spec">Specification</button>
          <button class="modal-btn" data-t="">Cancel</button>
        </div>
      </div>`
    back.querySelector('.doctype-file').textContent = filename
    const im = back.querySelector('.doctype-thumb')
    if (im && thumb) im.src = thumb
    const done = (val) => { try { document.body.removeChild(back) } catch {} ; resolve(val) }
    back.addEventListener('click', (e) => {
      if (e.target === back) return done(null)
      const b = e.target.closest('button[data-t]')
      if (b) done(b.dataset.t || null)
    })
    document.body.appendChild(back)
  })
}

/**
 * Open a PDF (or .bpx) file in a new tab.
 * Matches Qt: each PDF -> its own tab. .bpx files are handled as zip.
 */
async function _openFileInNewTab(file) {
  const lowerName = file.name.toLowerCase()

  // Handle .bpx (Bluebeam package — zip containing PDFs)
  if (lowerName.endsWith('.bpx')) {
    await _openBpxFile(file)
    return
  }

  // Regular PDF
  if (lowerName.endsWith('.pdf')) {
    await _openPdfInNewTab(file)
    return
  }

  log(`Unsupported file type: ${file.name}`)
}

// ── Specifications library (welcome screen, per-user constant context) ────

const specsPanel     = document.getElementById('specs-panel')
const specsList      = document.getElementById('specs-list')
const specsAddBtn    = document.getElementById('specs-add-btn')
const specsFileInput = document.getElementById('specs-file-input')

function _clearSpecs() {
  if (specsPanel) specsPanel.hidden = true
  if (specsList) specsList.innerHTML = ''
}

async function _loadSpecs() {
  if (!specsPanel || !specsList) return
  try {
    const { specs } = await api.listSpecs()
    _renderSpecs(specs || [])
  } catch {
    _clearSpecs()   // not signed in / transient
  }
}

function _renderSpecs(specs) {
  if (!specsPanel || !specsList) return
  specsPanel.hidden = false   // always show when signed in, so the user can add one
  specsList.innerHTML = ''
  if (!specs.length) {
    specsList.innerHTML = '<p class="specs-empty">No specs yet — add your spec book so the assistant can always cite it.</p>'
    return
  }
  for (const s of specs) {
    const item = document.createElement('div')
    item.className = 'spec-item'
    item.title = s.filename || 'spec'

    const name = document.createElement('a')
    name.className = 'spec-name'
    name.href = `/api/spec/${s.spec_id}/download`
    name.target = '_blank'
    name.rel = 'noopener'
    name.textContent = s.filename || 'spec.pdf'

    const meta = document.createElement('span')
    meta.className = 'spec-meta'
    meta.textContent = s.pages ? `${s.pages} pg` : ''

    const del = document.createElement('button')
    del.className = 'spec-remove'
    del.title = 'Remove from your library'
    del.innerHTML = '&times;'
    del.addEventListener('click', async (e) => {
      e.stopPropagation()
      try { await api.deleteSpec(s.spec_id) } catch {}
      await _loadSpecs()
    })

    item.appendChild(name)
    item.appendChild(meta)
    item.appendChild(del)
    specsList.appendChild(item)
  }
}

specsAddBtn?.addEventListener('click', () => specsFileInput?.click())
specsFileInput?.addEventListener('change', async () => {
  const file = specsFileInput.files?.[0]
  specsFileInput.value = ''
  if (!file) return
  if (specsAddBtn) { specsAddBtn.disabled = true; specsAddBtn.textContent = 'Uploading…' }
  try {
    await api.uploadSpec(file)
    await _loadSpecs()
  } catch (err) {
    log(`Spec upload failed: ${err.message}`)
    alert(`Spec upload failed: ${err.message}`)
  } finally {
    if (specsAddBtn) { specsAddBtn.disabled = false; specsAddBtn.textContent = '+ Add spec' }
  }
})

// ── Recent-jobs menu (welcome screen, last 30 days) ───────────────────────

const recentJobsPanel = document.getElementById('recent-jobs-panel')
const recentJobsList  = document.getElementById('recent-jobs-list')
let _recentJobsTimer = null

function _clearRecentJobs() {
  clearTimeout(_recentJobsTimer)
  _recentJobsTimer = null
  if (recentJobsPanel) recentJobsPanel.hidden = true
  if (recentJobsList) recentJobsList.innerHTML = ''
  document.body.classList.remove('has-recents')
}

// Last non-empty recents render; kept so a transient empty/failed refresh never
// blanks a populated panel (the post-preprocess flicker).
let _lastGoodJobs = []

/** Refresh shortly after an upload/preprocess so the manifest write settles. */
function _refreshRecentJobsSoon() {
  clearTimeout(_recentJobsTimer)
  _recentJobsTimer = setTimeout(() => { _loadRecentJobs(); refreshRecents() }, 1200)
}

async function _loadRecentJobs() {
  if (!recentJobsPanel || !recentJobsList) return
  try {
    const jobs = await api.listRecentJobs(30, 50)
    _renderRecentJobs(jobs)
  } catch {
    // Transient (auth blip / manifest not yet settled after an upload). Do NOT
    // blank a populated panel -- leave the current list; a later refresh restores
    // it authoritatively. Blanking here caused the post-preprocess flicker.
  }
}

function _renderRecentJobs(jobs) {
  const visible = Array.isArray(jobs)
    ? jobs.filter(j => !_deletedDocIds.has(j.doc_id))
    : []
  if (visible.length === 0) {
    // A just-uploaded doc's manifest can read back empty for a moment. Only
    // clear when we genuinely had nothing (deletes clear their own DOM).
    if (_lastGoodJobs.length === 0) _clearRecentJobs()
    return
  }
  _lastGoodJobs = visible
  recentJobsPanel.hidden = false
  document.body.classList.add('has-recents')
  recentJobsList.innerHTML = ''
  for (const job of visible) {
    const item = document.createElement('div')
    item.className = 'recent-job'
    item.dataset.docId = job.doc_id || ''
    item.title = `${job.filename} — reopen from cache`

    const top = document.createElement('div')
    top.className = 'recent-job-top'

    const name = document.createElement('div')
    name.className = 'recent-job-name'
    name.textContent = job.filename || job.doc_id

    const actions = document.createElement('div')
    actions.className = 'recent-job-actions'

    const renameBtn = document.createElement('button')
    renameBtn.className = 'recent-job-action'
    renameBtn.type = 'button'
    renameBtn.title = 'Rename cached job'
    renameBtn.textContent = 'Rename'
    renameBtn.addEventListener('click', async (e) => {
      e.stopPropagation()
      await _renameRecentJob(job)
    })

    const deleteBtn = document.createElement('button')
    deleteBtn.className = 'recent-job-action danger'
    deleteBtn.type = 'button'
    deleteBtn.title = 'Delete cached job'
    deleteBtn.textContent = 'Delete'
    deleteBtn.addEventListener('click', async (e) => {
      e.stopPropagation()
      await _deleteRecentJob(job)
    })

    actions.appendChild(renameBtn)
    actions.appendChild(deleteBtn)
    top.appendChild(name)
    top.appendChild(actions)

    const meta = document.createElement('div')
    meta.className = 'recent-job-meta'
    const badge = document.createElement('span')
    badge.className = `recent-job-badge ${job.ready ? 'ready' : 'pending'}`
    badge.textContent = job.ready ? 'Ready' : 'Processing'
    const info = document.createElement('span')
    const pages = job.page_count ? `${job.page_count} pg` : ''
    info.textContent = [pages, _relativeTime(job.created_at)].filter(Boolean).join(' \u00b7 ')
    meta.appendChild(badge)
    meta.appendChild(info)

    item.appendChild(top)
    item.appendChild(meta)
    item.addEventListener('click', () => selectProject(job.doc_id, job.filename))
    recentJobsList.appendChild(item)
  }
}

async function _renameRecentJob(job) {
  const current = job.filename || ''
  const next = window.prompt('Rename cached job', current)
  if (next == null) return
  const filename = next.trim()
  if (!filename || filename === current) return
  try {
    await api.renameDocument(job.doc_id, filename)
    _updateOpenTabsForRenamedDocument(job.doc_id, filename)
    await _loadRecentJobs()
    log(`Renamed cached job: ${current || job.doc_id} -> ${filename}`)
  } catch (err) {
    log(`Could not rename cached job: ${err.message}`)
  }
}

async function _deleteRecentJob(job) {
  const label = job.filename || job.doc_id
  const ok = window.confirm(
    `Delete "${label}" from cached jobs?\n\nThis removes the cached PDF and stored artifacts for this document.`
  )
  if (!ok) return
  // Optimistic UI: the plan vanishes from the screen the instant the user
  // confirms, regardless of how (or how slowly) the server request resolves.
  // _deletedDocIds keeps it filtered on every subsequent render, so it can't
  // be resurrected by an in-flight recent-list fetch.
  _deletedDocIds.add(job.doc_id)
  _closeTabsForDocument(job.doc_id)
  _removeRecentJobFromPanel(job.doc_id)
  try {
    await api.deleteDocument(job.doc_id)
    log(`Deleted cached job: ${label}`)
  } catch (err) {
    if (err && err.status === 404) {
      log(`Removed already-deleted job: ${label}`)
    } else {
      // Keep it hidden this session (the user asked for it gone) but surface
      // the failure so a genuine server-side delete error is visible.
      log(`Delete request failed for "${label}": ${err?.message ?? err} (hidden locally; may reappear after reload)`)
    }
  }
  await _loadRecentJobs()
}

function _removeRecentJobFromPanel(docId) {
  if (!recentJobsList || !docId) return
  for (const item of [...recentJobsList.querySelectorAll('.recent-job')]) {
    if (item.dataset.docId === docId) item.remove()
  }
  if (recentJobsList.children.length === 0) _clearRecentJobs()
}

function _relativeTime(iso) {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (!then) return ''
  const mins = Math.floor((Date.now() - then) / 60000)
  if (mins < 1)  return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)  return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

/** Unified project open: the plan and its chat are one unit. Clicking a plan in
 *  the left menu OR its chat chip both call this. The plan opens in its viewer
 *  tab and its per-project chat rides ON TOP of the plan (never on welcome). */
function selectProject(docId, filename, revealChat = false) {
  if (!docId) return
  _openDocById(docId, filename)      // plan opens first (sets doc-active)
  // Always bind the chat to this project (switches the widget out of home mode);
  // minimized on a plain plan-open, revealed only when it came from a chat chip.
  openProjectChat(docId, filename, revealChat)
}

/** Reopen a cached job by doc_id — pulls the PDF from Spaces, no re-upload. */
async function _openDocById(docId, filename) {
  const existingTabId = _findOpenDocumentTabId(docId)
  if (existingTabId) {
    await _switchToTab(existingTabId)
    return existingTabId
  }
  if (_openingCachedDocIds.has(docId)) return
  _openingCachedDocIds.add(docId)
  try {
    log(`Loading cached job: ${filename || docId}`)
    const { manifest } = await _prepareRenderedViewer(docId)
    return await _openRenderedDocInNewTab(manifest, filename || manifest.filename, docId)
  } catch (err) {
    log(`Could not reopen job: ${err.message}`)
    console.error('_openDocById error:', err)
  } finally {
    _openingCachedDocIds.delete(docId)
  }
}

function _useRenderedViewerForFile(file) {
  return !!file && file.name?.toLowerCase().endsWith('.pdf')
}

function _showViewerPrepOverlay(message = 'Preparing plan view...') {
  _hideViewerPrepOverlay()
  const overlayEl = document.createElement('div')
  overlayEl.className = 'modal-overlay viewer-prep-overlay'
  overlayEl.innerHTML = `
    <div class="modal-box" role="status" aria-live="polite">
      <h3 style="margin:0 0 10px;">Preparing plan view</h3>
      <p class="viewer-prep-message" style="margin:0 0 12px;color:var(--muted);line-height:1.45;">${message}</p>
      <div style="height:8px;background:rgba(255,255,255,0.12);border-radius:999px;overflow:hidden;">
        <div class="viewer-prep-bar" style="height:100%;width:0%;background:#2a7fff;transition:width .18s ease;"></div>
      </div>
      <p class="viewer-prep-note" style="margin:12px 0 0;color:var(--muted);font-size:9pt;">
        Opening the first sheet first. Thumbnails warm in the background; full sheets render as you view them.
      </p>
    </div>
  `
  document.body.appendChild(overlayEl)
  _viewerPrepOverlay = overlayEl
}

function _updateViewerPrepOverlay(message, done = 0, total = 0) {
  if (!_viewerPrepOverlay) return
  const msgEl = _viewerPrepOverlay.querySelector('.viewer-prep-message')
  const barEl = _viewerPrepOverlay.querySelector('.viewer-prep-bar')
  if (msgEl && message) msgEl.textContent = message
  if (barEl) {
    const pct = total ? Math.max(4, Math.min(100, Math.round((done / total) * 100))) : 12
    barEl.style.width = `${pct}%`
  }
}

function _hideViewerPrepOverlay() {
  if (!_viewerPrepOverlay) return
  _viewerPrepOverlay.remove()
  _viewerPrepOverlay = null
}

async function _prepareRenderedViewer(docId, opts = {}) {
  const { switchTabId = null } = opts
  _showViewerPrepOverlay('Preparing this plan for clean viewing...')
  panelCategories.classList.remove('hidden')
  categoriesList.innerHTML = '<p class="meta-empty">Preparing clean page images...</p>'
  if (classifyProgressBar) classifyProgressBar.style.display = 'block'
  if (classifyBar) classifyBar.style.width = '0%'
  if (classifyStatus) classifyStatus.textContent = 'Preparing high-quality plan view...'

  try {
    const final = await api.prepareDocumentViewer(docId, {
      onStatus: (s) => {
        const msg = s.message || 'Preparing high-quality plan view...'
        if (classifyStatus) classifyStatus.textContent = msg
        _updateViewerPrepOverlay(msg, 0, 0)
      },
      onProgress: (p) => {
        const pct = p.total ? Math.round((p.done / p.total) * 100) : 0
        const msg = p.message || `Rendering first sheet... ${p.done} / ${p.total} pages`
        if (classifyBar) classifyBar.style.width = `${pct}%`
        if (classifyStatus) classifyStatus.textContent = msg
        _updateViewerPrepOverlay(msg, p.done, p.total)
      },
    })

    if (classifyBar) classifyBar.style.width = '100%'
    if (classifyStatus) classifyStatus.textContent = 'Plan ready. Loading viewer...'
    _updateViewerPrepOverlay('Plan ready. Loading viewer...', 1, 1)

    const manifest = await api.getDocumentViewer(docId)
    if (switchTabId && switchTabId === _activeTabId) {
      await _switchActiveTabToRenderedViewer(switchTabId, manifest, docId)
    }
    return { final, manifest }
  } finally {
    _hideViewerPrepOverlay()
  }
}

async function _switchActiveTabToRenderedViewer(tabId, manifest, docId) {
  if (_activeTabId !== tabId) return
  const currentTab = _tabs.find(t => t.id === tabId)
  if (!currentTab || currentTab.type !== 'document') return

  const oldPdf = _pdf
  _pdf = await viewer.loadRendered(manifest)
  if (oldPdf && oldPdf !== _pdf && typeof oldPdf.destroy === 'function') {
    oldPdf.destroy().catch(() => {})
  }

  state.resetDocument()
  state.update({ pageCount: _pdf.numPages, currentPage: 0, docId })
  _startViewerPerfPolling(docId)

  overlay.attach(pageWrapper)
  overlay.onAnnotationDrawn((rect, o) => _onBboxDrawn(rect, o || {}))
  overlay.setExtractTableCallback(() => _exportTable())
  overlay.setOnBboxCleared(() => { _lastDrawnBbox = null; document.body.classList.remove('draw-mode-active') })

  btnDrawBbox.disabled = false
  btnTextSelect.disabled = false
  _setTextSelectMode(false)

  search.clearCache()
  _clearSearch()
  _updatePageLabel()
  _updateNavButtons()
  _updateMetadata(_fileName, _pdf.numPages)
  _buildPageStrip(_pdf)

  currentTab.file = null
  currentTab.docId = docId
  currentTab.viewerState = viewer.saveState()
  currentTab.appState = _saveAppState()

  log('Switched to high-quality rendered viewer')
}

async function _openRenderedDocInNewTab(manifest, filename, docId) {
  _saveCurrentTab()

  const id = _nextTabId()
  const tabLabel = _tabLabelFromFilename(filename || manifest.filename || 'Document')
  const tab = {
    id,
    label: tabLabel,
    type: 'document',
    file: null,
    docId,
    viewerState: null,
    appState: null,
    pages: null,
  }
  _tabs.push(tab)
  _activeTabId = id
  _renderTabBar()

  _fileName = filename || manifest.filename || 'document.pdf'
  _pdf = null
  _searchResults = []
  _searchFlat = []
  _searchFlatIdx = -1

  try {
    welcomeEl.classList.remove('active')
    scrollContainer.classList.remove('hidden')
    searchBar.style.display = 'flex'

    _pdf = await viewer.loadRendered(manifest)
    const pageCount = _pdf.numPages
    state.resetDocument()
    state.update({ pageCount, currentPage: 0, docId })
    _startViewerPerfPolling(docId)

    overlay.attach(pageWrapper)
    overlay.onAnnotationDrawn((rect, o) => _onBboxDrawn(rect, o || {}))
    overlay.setExtractTableCallback(() => _exportTable())
    overlay.setOnBboxCleared(() => { _lastDrawnBbox = null; document.body.classList.remove('draw-mode-active') })
    btnDrawBbox.disabled = false
    btnTextSelect.disabled = false
    _setTextSelectMode(false)

    _updatePageLabel()
    _updateNavButtons()
    search.clearCache()
    _clearSearch()
    _updateMetadata(_fileName, pageCount)
    _buildPageStrip(_pdf)

    log(`Rendered viewer loaded: ${pageCount} page(s)`)
    _runPreprocessing(docId)
    _loadSavedTables(docId)
    return id
  } catch (err) {
    log(`Error opening rendered viewer: ${err.message}`)
    console.error('_openRenderedDocInNewTab error:', err)
    _tabs = _tabs.filter(t => t.id !== id)
    _activeTabId = _tabs[_tabs.length - 1]?.id ?? ''
    _renderTabBar()
    _activateTab(_activeTabId)
    return null
  }
}

/**
 * Open a .bpx file (Bluebeam zip package).
 * Extracts embedded PDFs and lets user choose if multiple.
 */
async function _openBpxFile(file) {
  try {
    const { default: JSZip } = await import('jszip')
    const zip = await JSZip.loadAsync(await file.arrayBuffer())

    // Find all PDFs inside the zip
    const pdfEntries = []
    zip.forEach((path, entry) => {
      if (!entry.dir && path.toLowerCase().endsWith('.pdf')) {
        pdfEntries.push({ path, entry })
      }
    })

    if (pdfEntries.length === 0) {
      log(`No PDFs found in ${file.name}`)
      return
    }

    let chosen = pdfEntries[0]
    if (pdfEntries.length > 1) {
      // Show chooser dialog (matches Qt _choose_bpx_pdf)
      const chosenPath = await _showBpxChooser(file.name, pdfEntries.map(e => e.path))
      if (!chosenPath) return
      chosen = pdfEntries.find(e => e.path === chosenPath) || pdfEntries[0]
    }

    const pdfData = await chosen.entry.async('arraybuffer')
    const pdfBlob = new Blob([pdfData], { type: 'application/pdf' })
    const baseName = chosen.path.split('/').pop() || file.name.replace(/\.bpx$/i, '.pdf')
    const pdfFile  = new File([pdfBlob], baseName, { type: 'application/pdf' })
    await _openPdfInNewTab(pdfFile)
  } catch (err) {
    log(`Error opening BPX: ${err.message}`)
    console.error(err)
  }
}

/**
 * Show a chooser dialog for .bpx packages with multiple PDFs.
 * If showKeepOuter is true, adds "Keep current document" option.
 * Returns the chosen path, '__keep_outer__', or null (cancel).
 */
function _showBpxChooser(bpxName, paths, showKeepOuter = false) {
  return new Promise((resolve) => {
    const dialog = document.createElement('div')
    dialog.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(0,0,0,0.6); display: flex; align-items: center;
      justify-content: center; z-index: 10000;
    `
    const box = document.createElement('div')
    box.style.cssText = `
      background: #2d2d2d; border: 1px solid #3e3e3e; border-radius: 8px;
      padding: 16px; max-width: 500px; width: 90%; max-height: 400px;
      color: #e0e0e0; font-family: 'Segoe UI', sans-serif;
    `
    box.innerHTML = `<p style="margin-bottom:12px;font-weight:600;">This PDF contains embedded documents. Choose which to open:</p>`
    const list = document.createElement('div')
    list.style.cssText = 'max-height:300px;overflow-y:auto;'

    // "Keep current document" option at the top
    if (showKeepOuter) {
      const keepItem = document.createElement('div')
      keepItem.textContent = `\u25B6  Keep current document (${bpxName})`
      keepItem.style.cssText = `
        padding: 8px 12px; cursor: pointer; border-radius: 4px;
        font-family: 'Segoe UI', sans-serif; font-size: 10pt;
        color: #7cb7ff; font-weight: 600; margin-bottom: 4px;
      `
      keepItem.addEventListener('mouseenter', () => keepItem.style.background = '#404040')
      keepItem.addEventListener('mouseleave', () => keepItem.style.background = 'transparent')
      keepItem.addEventListener('click', () => { document.body.removeChild(dialog); resolve('__keep_outer__') })
      list.appendChild(keepItem)
    }

    for (const p of paths) {
      const item = document.createElement('div')
      item.textContent = p
      item.style.cssText = `
        padding: 8px 12px; cursor: pointer; border-radius: 4px;
        font-family: Consolas, monospace; font-size: 10pt;
      `
      item.addEventListener('mouseenter', () => item.style.background = '#404040')
      item.addEventListener('mouseleave', () => item.style.background = 'transparent')
      item.addEventListener('click', () => { document.body.removeChild(dialog); resolve(p) })
      list.appendChild(item)
    }
    box.appendChild(list)
    const cancelBtn = document.createElement('button')
    cancelBtn.textContent = 'Cancel'
    cancelBtn.style.cssText = `
      margin-top: 12px; padding: 6px 16px; background: #3e3e3e;
      border: 1px solid #505050; color: #e0e0e0; border-radius: 4px; cursor: pointer;
    `
    cancelBtn.addEventListener('click', () => { document.body.removeChild(dialog); resolve(null) })
    box.appendChild(cancelBtn)
    dialog.appendChild(box)
    dialog.addEventListener('click', (e) => { if (e.target === dialog) { document.body.removeChild(dialog); resolve(null) } })
    document.body.appendChild(dialog)
  })
}

async function _openPdfInNewTab(file, skipBluebeamCheck = false, existingDocId = null, chatProject = null, viewOnly = false) {
  _saveCurrentTab()

  // Reopening a cached job: never re-run the Bluebeam upload check.
  if (existingDocId) skipBluebeamCheck = true
  let uploadedDocId = existingDocId

  const id = _nextTabId()
  const tabLabel = _tabLabelFromFilename(file.name)
  /** @type {Tab} */
  const tab = {
    id,
    label: tabLabel,
    type: 'document',
    file,
    docId: existingDocId,
    chatProjectId: chatProject?.docId || null,      // supporting-doc tab -> parent plan's chat
    chatProjectLabel: chatProject?.label || null,
    viewOnly,                                        // supporting doc: no plan classification UI
    viewDoc: chatProject?.viewing || null,           // {kind, filename, id} for the chat context
    viewerState: null,
    appState: null,
    pages: null,
  }
  _tabs.push(tab)
  _activeTabId = id
  _renderTabBar()

  if (viewOnly) {
    // Supporting document (proposal/addendum/spec): it is NOT the plan, so drop
    // every piece of plan classification state so none of it renders over it.
    _classifyResults = null
    _callouts = null
    _calloutHighlightMap = null
    viewer.setPageFilter(null)
    panelCategories.classList.add('hidden')
  }

  _fileName = file.name
  _pdf = null
  _searchResults = []
  _searchFlat    = []
  _searchFlatIdx = -1

  log(`Opening: ${file.name}`)

  try {
    welcomeEl.classList.remove('active')
    document.body.classList.add('doc-active')   // hide the chat greeting/recents over the plan
    scrollContainer.classList.remove('hidden')
    searchBar.style.display = 'flex'

    log(`Loading PDF (${(file.size / 1048576).toFixed(0)} MB)...`)

    // ── Large PDF path ─────────────────────────────────────────────────────
    // Upload once, then check/render from the saved server-side PDF. This avoids
    // a brutal double upload for 500MB+ plan sets.
    let suspectedBluebeam = false
    if (!skipBluebeamCheck && !uploadedDocId && file.size > 50 * 1024 * 1024 && _useRenderedViewerForFile(file)) {
      let earlyOverlay = document.createElement('div')
      earlyOverlay.style.cssText = `
        position: fixed; top: 0; left: 0; width: 100%; height: 100%;
        background: rgba(0,0,0,0.65); display: flex; align-items: center;
        justify-content: center; z-index: 10000; color: #e0e0e0;
        font-family: 'Segoe UI', sans-serif; font-size: 14pt;
        flex-direction: column; gap: 12px;
      `
      earlyOverlay.innerHTML = `
        <div style="font-weight:600;font-size:16pt;">Uploading plan</div>
        <div id="bb-early-status" style="font-size:11pt;color:#aaa;">Uploading ${(file.size / 1048576).toFixed(0)} MB...</div>
        <div style="width:300px;height:6px;background:#333;border-radius:3px;overflow:hidden;">
          <div id="bb-early-bar" style="width:0%;height:100%;background:#4fc3f7;transition:width 0.2s;"></div>
        </div>
      `
      document.body.appendChild(earlyOverlay)
      console.error(`[BB] Large file (${(file.size/1048576).toFixed(0)} MB) - uploading once before PDF.js load`)

      try {
        const uploadRes = await api.uploadDocument(file, {
          timeout: 3600000,
          onProgress: (e) => {
            if (e.lengthComputable) {
              const pct = Math.round(e.loaded / e.total * 100)
              const bar = document.getElementById('bb-early-bar')
              const status = document.getElementById('bb-early-status')
              if (bar) bar.style.width = pct + '%'
              if (status) status.textContent = pct < 100
                ? `Uploading... ${(e.loaded/1048576).toFixed(0)} / ${(e.total/1048576).toFixed(0)} MB (${pct}%)`
                : 'Saving upload...'
            }
          },
        })
        uploadedDocId = uploadRes.doc_id
        state.update({ docId: uploadedDocId })
        const currentTab = _tabs.find(t => t.id === id)
        if (currentTab) currentTab.docId = uploadedDocId
        openProjectChat(uploadedDocId, file.name, false)   // bind chat to this plan (minimized)
        _refreshRecentJobsSoon()

        const status = document.getElementById('bb-early-status')
        if (status) status.textContent = 'Checking saved PDF for embedded documents...'
        const checkData = skipBluebeamCheck
          ? { is_bluebeam: false, embedded: [] }
          : await api.checkDocumentBluebeam(uploadedDocId)

        console.error('[BB] Early server check result:', checkData)

        if (checkData && checkData.is_bluebeam && checkData.embedded?.length > 0) {
          suspectedBluebeam = true
          document.body.removeChild(earlyOverlay); earlyOverlay = null
          const names = checkData.embedded.map(e => e.name)
          const chosenName = await _showBpxChooser(file.name, names, true)

          if (chosenName && chosenName !== '__keep_outer__') {
            const extractOverlay = document.createElement('div')
            extractOverlay.style.cssText = `
              position: fixed; top: 0; left: 0; width: 100%; height: 100%;
              background: rgba(0,0,0,0.65); display: flex; align-items: center;
              justify-content: center; z-index: 10000; color: #e0e0e0;
              font-family: 'Segoe UI', sans-serif; font-size: 16pt; font-weight: 600;
            `
            extractOverlay.textContent = 'Extracting embedded PDF…'
            document.body.appendChild(extractOverlay)
            try {
              const chosenEmb = checkData.embedded.find(e => e.name === chosenName) || checkData.embedded[0]
              const embeddedDoc = await api.extractDocumentBluebeamToDocument(uploadedDocId, chosenEmb.index)
              document.body.removeChild(extractOverlay)
              uploadedDocId = embeddedDoc.doc_id
              state.update({ docId: uploadedDocId })
              const currentTab = _tabs.find(t => t.id === id)
              if (currentTab) {
                currentTab.docId = uploadedDocId
                currentTab.label = _tabLabelFromFilename(embeddedDoc.filename || chosenEmb.name)
              }
              _renderTabBar()
              _refreshRecentJobsSoon()
              log(`Preparing rendered plan view (doc_id: ${uploadedDocId})`)
              await _prepareRenderedViewer(uploadedDocId, { switchTabId: id })
              _runPreprocessing(uploadedDocId)
              return
            } catch (exErr) {
              document.body.removeChild(extractOverlay)
              log(`Extraction failed: ${exErr.message}`)
              console.error('[BB] Extraction error:', exErr)
            }
          }
          // User chose "Keep outer" — fall through and load the wrapper in PDF.js normally
        } else if (checkData && !checkData.is_bluebeam) {
          // Confirmed NOT bluebeam - open the rendered viewer from the saved upload.
          document.body.removeChild(earlyOverlay); earlyOverlay = null
          log(`Preparing rendered plan view (doc_id: ${uploadedDocId})`)
          await _prepareRenderedViewer(uploadedDocId, { switchTabId: id })
          _runPreprocessing(uploadedDocId)
          return
        } else {
          // null / unexpected - remove overlay and load normally
          if (earlyOverlay) { document.body.removeChild(earlyOverlay); earlyOverlay = null }
        }
      } catch (earlyErr) {
        if (earlyOverlay) { try { document.body.removeChild(earlyOverlay) } catch {} earlyOverlay = null }
        log(`Large-file upload/prep failed - falling back to live PDF view: ${earlyErr.message}`)
        console.error('[BB] Large-file upload/prep failed:', earlyErr)
      }
    }

    _pdf = await viewer.loadPdf(file)
    log(`PDF loaded: ${_pdf.numPages} page(s)`)

    // ── Bluebeam embedded PDF detection (small files only — large files checked above) ──
    if (!skipBluebeamCheck && file.size <= 50 * 1024 * 1024) {
      let bluebeamHandled = false

      // 1) Try PDF.js getAttachments() first (works for standard EmbeddedFiles)
      try {
        log('Checking PDF.js attachments...')
        const attachments = await _pdf.getAttachments()
        log(`PDF.js attachments: ${attachments ? Object.keys(attachments).length : 0}`)
        if (attachments) {
          const pdfAttachments = Object.entries(attachments).filter(([, att]) => {
            if (att.filename?.toLowerCase().endsWith('.pdf')) return true
            // Check PDF magic bytes (%PDF)
            if (att.content && att.content.length > 4 &&
                att.content[0] === 0x25 && att.content[1] === 0x50 &&
                att.content[2] === 0x44 && att.content[3] === 0x46) return true
            return false
          })

          if (pdfAttachments.length > 0) {
            log(`Bluebeam package detected (client-side): ${pdfAttachments.length} embedded PDF(s)`)
            bluebeamHandled = true

            const names = pdfAttachments.map(([key, att]) => att.filename || key)
            const chosenName = await _showBpxChooser(file.name, names, true)

            if (chosenName && chosenName !== '__keep_outer__') {
              const chosenEntry = pdfAttachments.find(([key, att]) => (att.filename || key) === chosenName) || pdfAttachments[0]
              const [, att] = chosenEntry
              const embeddedFile = new File(
                [att.content],
                att.filename || 'embedded.pdf',
                { type: 'application/pdf' }
              )

              _pdf.destroy()
              _pdf = null
              viewer.detach()
              _tabs = _tabs.filter(t => t.id !== id)

              _activeTabId = _tabs[_tabs.length - 1]?.id ?? ''
              _renderTabBar()
              _activateTab(_activeTabId)

              await _openPdfInNewTab(embeddedFile, true)
              return
            }
            // User chose "Keep current document" — continue with outer PDF
          }
        }
      } catch (attErr) {
        log(`Client-side attachment check failed: ${attErr.message}`)
        console.warn('Client-side attachment check:', attErr)
      }

      // 2) Fallback: server-side fitz check for PDF Portfolios (Bluebeam)
      //    PDF.js can't read Collection/Portfolio attachments, but fitz can.
      //    Only check if client-side found nothing and the PDF is suspiciously short.
      log(`bluebeamHandled=${bluebeamHandled}, numPages=${_pdf.numPages}`)
      console.error(`[BB] bluebeamHandled=${bluebeamHandled}, numPages=${_pdf?.numPages}, skipBluebeamCheck=${skipBluebeamCheck}, file=${file.name} (${(file.size/1048576).toFixed(0)} MB)`)
      if (!bluebeamHandled && _pdf.numPages <= 5) {
        let overlay = null
        try {
          // Show a visible loading overlay so the user knows something is happening
          overlay = document.createElement('div')
          overlay.style.cssText = `
            position: fixed; top: 0; left: 0; width: 100%; height: 100%;
            background: rgba(0,0,0,0.65); display: flex; align-items: center;
            justify-content: center; z-index: 10000; color: #e0e0e0;
            font-family: 'Segoe UI', sans-serif; font-size: 14pt;
            flex-direction: column; gap: 12px;
          `
          overlay.innerHTML = `
            <div style="font-weight:600;font-size:16pt;">Checking for embedded documents…</div>
            <div id="bb-upload-status" style="font-size:11pt;color:#aaa;">Uploading ${(file.size / 1048576).toFixed(0)} MB…</div>
            <div style="width:300px;height:6px;background:#333;border-radius:3px;overflow:hidden;">
              <div id="bb-upload-bar" style="width:0%;height:100%;background:#4fc3f7;transition:width 0.2s;"></div>
            </div>
          `
          document.body.appendChild(overlay)

          log('Checking for Bluebeam portfolio (server-side)...')

          // Use XMLHttpRequest for upload progress reporting
          const checkData = await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest()
            xhr.open('POST', '/api/plans/check-bluebeam-upload')

            xhr.upload.addEventListener('progress', (e) => {
              if (e.lengthComputable) {
                const pct = Math.round(e.loaded / e.total * 100)
                const bar = document.getElementById('bb-upload-bar')
                const status = document.getElementById('bb-upload-status')
                if (bar) bar.style.width = pct + '%'
                if (status) status.textContent = pct < 100
                  ? `Uploading… ${(e.loaded / 1048576).toFixed(0)} / ${(e.total / 1048576).toFixed(0)} MB (${pct}%)`
                  : 'Analyzing PDF structure…'
              }
            })

            xhr.addEventListener('load', () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                try { resolve(JSON.parse(xhr.responseText)) }
                catch { resolve(null) }
              } else {
                resolve(null)
              }
            })
            xhr.addEventListener('error', () => resolve(null))
            xhr.addEventListener('timeout', () => resolve(null))
            xhr.timeout = 3600000  // 60 minutes

            const formData = new FormData()
            formData.append('file', file)
            xhr.send(formData)
          })

          if (checkData && checkData.is_bluebeam && checkData.embedded.length > 0) {
            suspectedBluebeam = true
            log(`Bluebeam portfolio detected (server-side): ${checkData.embedded.length} embedded PDF(s)`)
            if (overlay) { document.body.removeChild(overlay); overlay = null }

            const names = checkData.embedded.map(e => e.name)
            const chosenName = await _showBpxChooser(file.name, names, true)

            if (chosenName && chosenName !== '__keep_outer__') {
              // Show extraction overlay
              const extractOverlay = document.createElement('div')
              extractOverlay.style.cssText = `
                position: fixed; top: 0; left: 0; width: 100%; height: 100%;
                background: rgba(0,0,0,0.65); display: flex; align-items: center;
                justify-content: center; z-index: 10000; color: #e0e0e0;
                font-family: 'Segoe UI', sans-serif; font-size: 16pt; font-weight: 600;
              `
              extractOverlay.textContent = 'Extracting embedded PDF…'
              document.body.appendChild(extractOverlay)

              try {
                const chosenEmb = checkData.embedded.find(e => e.name === chosenName) || checkData.embedded[0]
                const extractUrl = `/api/plans/extract-embedded-upload/${checkData.token}?index=${chosenEmb.index}`
                const extractResp = await fetch(extractUrl)
                if (!extractResp.ok) throw new Error(`Extract failed: ${extractResp.statusText}`)
                const blob = await extractResp.blob()
                const embeddedFile = new File([blob], chosenEmb.name, { type: 'application/pdf' })

                _pdf.destroy()
                _pdf = null
                viewer.detach()
                _tabs = _tabs.filter(t => t.id !== id)

                _activeTabId = _tabs[_tabs.length - 1]?.id ?? ''
                _renderTabBar()
                _activateTab(_activeTabId)

                document.body.removeChild(extractOverlay)
                await _openPdfInNewTab(embeddedFile, true)
                return
              } catch (exErr) {
                document.body.removeChild(extractOverlay)
                log(`Extraction failed: ${exErr.message}`)
                console.error(exErr)
              }
            }
            // User chose "Keep current document" — continue with outer PDF
          } else {
            if (overlay) { document.body.removeChild(overlay); overlay = null }
          }
        } catch (srvErr) {
          if (overlay) { try { document.body.removeChild(overlay) } catch {} }
          console.error('[BB] Server-side bluebeam check FAILED:', srvErr)
        }
      }
    }

    const pageCount = _pdf.numPages
    state.resetDocument()
    state.update({ pageCount, currentPage: 0 })

    overlay.attach(pageWrapper)
    overlay.onAnnotationDrawn((rect, opts) => _onBboxDrawn(rect, opts || {}))
    overlay.setExtractTableCallback(() => _exportTable())
    overlay.setOnBboxCleared(() => { _lastDrawnBbox = null; document.body.classList.remove('draw-mode-active') })
    btnDrawBbox.disabled = !!viewOnly
    btnTextSelect.disabled = false

    _updatePageLabel()
    _updateNavButtons()
    search.clearCache()
    _clearSearch()
    _updateMetadata(file.name, pageCount)
    _buildPageStrip(_pdf)
    _initThumbPool()   // pre-warm 3 thumbnail workers in background

    log(`${pageCount} pages loaded`)

    // Upload to backend, then auto-classify in background
    // Skip both upload and auto-classify if this looks like an unresolved Bluebeam wrapper
    if (viewOnly) {
      // Supporting document (proposal/addendum/spec) opened for READING only --
      // never upload it as a plan or queue preprocessing.
      log('Opened for viewing')
    } else if (uploadedDocId) {
      // Reopened from the recent-jobs menu — the PDF and graph are already
      // cached in Spaces, so just wire up the doc id and load cached results.
      state.update({ docId: uploadedDocId })
      log(`Using existing cached document (doc_id: ${uploadedDocId})`)
      _runPreprocessing(uploadedDocId)
    } else if (suspectedBluebeam) {
      console.error('[BB] Skipping upload+classify — this appears to be a Bluebeam wrapper page')
      log('Bluebeam wrapper detected — skipping classification')
    } else {
    api.uploadDocument(file).then(async res => {
      state.update({ docId: res.doc_id })
      const currentTab = _tabs.find(t => t.id === id)
      if (currentTab) currentTab.docId = res.doc_id
      openProjectChat(res.doc_id, file.name, false)   // bind chat to this plan (minimized)
      log(`Ready for table extraction (doc_id: ${res.doc_id})`)
      _refreshRecentJobsSoon()
      // Run light-graph preprocessing (classification + callouts) in background
      _runPreprocessing(res.doc_id)
    }).catch(err => {
      log(`Backend upload failed — Extract Table unavailable: ${err.message}`)
    })
    } // end if (!suspectedBluebeam)
    return id
  } catch (err) {
    log(`Error opening PDF: ${err.message}`)
    console.error('_openPdfInNewTab error:', err)
    _tabs = _tabs.filter(t => t.id !== id)
    _activeTabId = _tabs[_tabs.length - 1]?.id ?? ''
    _renderTabBar()
    _activateTab(_activeTabId)
    return null
  }
}

// ── Drag-and-drop (matches Qt — accepts .pdf and .bpx) ───────────────────

const dropZone = document.getElementById('drop-zone')
const btnBrowse = document.getElementById('btn-browse')

// "Browse" link on the drop zone triggers the same file input as the toolbar button
btnBrowse?.addEventListener('click', () => fileInput.click())

document.addEventListener('dragenter', (e) => { e.preventDefault(); e.stopPropagation() })
document.addEventListener('dragover',  (e) => {
  e.preventDefault(); e.stopPropagation()
  // Highlight the welcome drop banner regardless of which element is under the
  // cursor (the chat overlay is a sibling of #welcome, so its own dragover
  // wouldn't fire over the banner).
  if (welcomeEl.classList.contains('active')) document.body.classList.add('file-dragging')
})
// Clear the drag highlight when the file leaves the window entirely.
document.addEventListener('dragleave', (e) => {
  if (e.relatedTarget === null) document.body.classList.remove('file-dragging')
})

// Visual feedback on welcome area + drop zone
welcomeEl.addEventListener('dragover', (e) => {
  e.preventDefault()
  e.stopPropagation()
  welcomeEl.classList.add('drag-over')
  document.body.classList.add('file-dragging')
  dropZone?.classList.add('drag-over')
})
welcomeEl.addEventListener('dragleave', () => {
  welcomeEl.classList.remove('drag-over')
  document.body.classList.remove('file-dragging')
  dropZone?.classList.remove('drag-over')
})

async function _handleDocumentDrop(e) {
  e.preventDefault()
  e.stopPropagation()
  welcomeEl.classList.remove('drag-over')
  document.body.classList.remove('file-dragging')
  dropZone?.classList.remove('drag-over')

  const files = e.dataTransfer?.files
  if (!files?.length) {
    log('Drop ignored: no file was provided by the browser')
    return
  }

  let opened = 0
  for (const file of files) {
    const ln = file.name.toLowerCase()
    if (ln.endsWith('.pdf') || ln.endsWith('.bpx')) {
      opened += 1
      log(`Dropped file: ${file.name}`)
      try {
        await _ingestFile(file)
      } catch (err) {
        log(`Drop failed for ${file.name}: ${err.message}`)
        console.error('drop ingest failed:', err)
      }
    }
  }
  if (!opened) {
    log('Drop ignored: please drop a .pdf or .bpx file')
  }
}

document.addEventListener('drop', _handleDocumentDrop, { capture: true })

// ── Metadata panel (matches Qt MetadataPanel — Title, File, Pages, Path, Author, Subject) ──

function _updateMetadata(filename, pageCount) {
  if (!filename) {
    metadataContent.innerHTML = `<p class="meta-empty">No document loaded</p>`
    return
  }
  const title = filename.replace(/\.(pdf|bpx)$/i, '')
  metadataContent.innerHTML = `
    <p><b>${title}</b></p>
    <p><b>File:</b> ${filename}</p>
    <p><b>Pages:</b> ${pageCount}</p>
  `
}

// ── Layer toggles (matches Qt InspectorPanel) ─────────────────────────────

layerText?.addEventListener('change',   () => overlay.setLayerVisible('text',   layerText.checked))
layerTables?.addEventListener('change', () => overlay.setLayerVisible('tables', layerTables.checked))
layerPredictions?.addEventListener('change', () => overlay.setLayerVisible('layout', layerPredictions.checked))

// ── Overlay refresh helper ────────────────────────────────────────────────

function _refreshOverlayData() {
  const s  = state.getState()
  const ps = state.getPageState(s.currentPage)
  overlay.beginBatch()
  try {
    overlay.setAnnotations(ps.annotations)
    overlay.setLayoutBoxes(ps.artifacts?.layout?.map(i => i.rect).filter(Boolean) ?? [])
    overlay.setTableBoxes(ps.artifacts?.tables?.map(i => i.rect).filter(Boolean) ?? [])
    overlay.setTextHighlights(ps.artifacts?.text?.map(i => i.rect).filter(Boolean) ?? [])
    overlay.setCalloutHighlights(_calloutHighlightMap?.[s.currentPage] ?? [])
  } finally {
    overlay.endBatch()
  }
  _refreshSearchHighlightsForPage(s.currentPage)
}

// ── Load page data (artifacts from backend, if available) ─────────────────

async function _loadPageData(pageIdx) {
  const s = state.getState()
  if (!s.docId) return

  try {
    const arts = await api.getPageArtifacts(s.docId, pageIdx)
    if (arts) {
      state.setPageArtifacts(pageIdx, arts)
      if (pageIdx === state.getState().currentPage) _refreshOverlayData()
    }
  } catch {}
}

// ── Search (searches PDF text layer via page.getTextContent) ──────────────

function openSearchBar() {
  searchInput.focus()
  searchInput.select()
}

function closeSearchBar() {
  _clearSearch()
  searchInput.value = ''
}

function _clearSearch() {
  search.cancel()
  _searchResults = []
  _searchFlat    = []
  _searchFlatIdx = -1
  searchCount.textContent = ''
  overlay.setSearchHighlights([], -1)
}

async function runSearch() {
  if (!_pdf) { searchCount.textContent = 'No PDF loaded'; return }
  if (viewer.isRenderedMode()) { searchCount.textContent = 'Search unavailable in rendered view'; return }
  const q = searchInput.value.trim()
  if (!q) { _clearSearch(); return }

  _clearSearch()
  searchCount.textContent = 'Searching...'
  log(`Searching for: "${q}" (${_pdf.numPages} pages)`)

  let jumped = false

  search.searchIncremental(_pdf, q,
    // onPageResult — called as each page with matches is found
    (result) => {
      _searchResults.push(result)
      for (const rect of result.rects) {
        _searchFlat.push({ page: result.page, rect })
      }
      searchCount.textContent = `${_searchFlat.length} found...`

      // Log what text was matched on this page
      if (result.texts) {
        log(`Page ${result.page + 1}: matched "${result.texts.join('", "')}"`)
      }

      // Jump to first match immediately
      if (!jumped) {
        jumped = true
        _searchFlatIdx = 0
        _gotoSearchMatch(0)
      }
    },
    // onDone — called when all pages are searched
    () => {
      if (_searchFlat.length === 0) {
        searchCount.textContent = 'No matches'
        log('No matches found')
      } else {
        searchCount.textContent = `${_searchFlatIdx + 1} / ${_searchFlat.length}`
        log(`${_searchFlat.length} match(es) found across ${_searchResults.length} pages`)
      }
    }
  )
}

function _gotoSearchMatch(flatIdx) {
  if (!_searchFlat.length) return
  flatIdx = ((flatIdx % _searchFlat.length) + _searchFlat.length) % _searchFlat.length
  _searchFlatIdx = flatIdx
  searchCount.textContent = `${flatIdx + 1} / ${_searchFlat.length}`

  const match = _searchFlat[flatIdx]
  const cur   = state.getState().currentPage

  if (match.page !== cur) {
    state.update({ currentPage: match.page })
    viewer.goToPage(match.page)
    _updatePageLabel()
    _updateNavButtons()
  }
  _refreshSearchHighlightsForPage(match.page)
}

function _refreshSearchHighlightsForPage(pageIdx) {
  const pageResult = _searchResults.find(r => r.page === pageIdx)
  if (!pageResult) { overlay.setSearchHighlights([], -1); return }

  const match = _searchFlat[_searchFlatIdx]
  let activeIdx = -1
  if (match && match.page === pageIdx) {
    activeIdx = pageResult.rects.indexOf(match.rect)
  }
  overlay.setSearchHighlights(pageResult.rects, activeIdx)
}

searchGo?.addEventListener('click', () => runSearch())
btnSearch?.addEventListener('click', () => { if (_pdf && !viewer.isRenderedMode()) openSearchBar() })
searchClose.addEventListener('click',   closeSearchBar)
searchPrevBtn.addEventListener('click', () => _gotoSearchMatch(_searchFlatIdx - 1))
searchNextBtn.addEventListener('click', () => _gotoSearchMatch(_searchFlatIdx + 1))

searchInput.addEventListener('keydown', e => {
  if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); runSearch() }
  if (e.key === 'Escape') { e.stopPropagation(); closeSearchBar() }
})

// ── Global keyboard shortcuts (matches Qt _build_shortcuts) ───────────────

document.addEventListener('keydown', e => {
  const tag = document.activeElement?.tagName?.toLowerCase()
  const inInput = tag === 'input' || tag === 'textarea'

  // Ctrl+F -- open PDF search (not browser search)
  if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
    e.preventDefault()
    e.stopImmediatePropagation()
    if (_pdf && !viewer.isRenderedMode()) openSearchBar()
    return
  }

  // Ctrl+W — close current tab
  if ((e.ctrlKey || e.metaKey) && e.key === 'w') {
    e.preventDefault()
    const tab = _tabs.find(t => t.id === _activeTabId)
    if (tab && tab.type !== 'welcome') _closeTab(_activeTabId)
    return
  }

  // Ctrl+Tab / Ctrl+Shift+Tab — cycle tabs
  if ((e.ctrlKey || e.metaKey) && e.key === 'Tab') {
    e.preventDefault()
    _cycleTab(e.shiftKey ? -1 : 1)
    return
  }

  // Esc — abort an active tool first (draw / drawn bbox / text-select), then
  // fall back to clearing search. (Escape must NOT exit zoom mode: the viewer
  // is always in scroll-to-zoom mode, and doing so reset the page and stranded
  // wheel zoom.)
  if (e.key === 'Escape') {
    if (_drawMode) { _setDrawMode(false); return }
    if (overlay.hasActiveBbox && overlay.hasActiveBbox()) { overlay.clearActiveBbox(); _lastDrawnBbox = null; return }
    if (_textSelectMode) { _setTextSelectMode(false); _hideCopyTooltip(); return }
    if (searchInput.value) { closeSearchBar(); return }
    return
  }

  // Don't handle navigation keys when typing in an input
  if (inInput) return

  // Arrow / Page navigation
  if (e.key === 'ArrowDown'  || e.key === 'PageDown') { e.preventDefault(); navigate(+1) }
  if (e.key === 'ArrowUp'    || e.key === 'PageUp')   { e.preventDefault(); navigate(-1) }
  if (e.key === 'ArrowRight')                          { e.preventDefault(); navigate(+1) }
  if (e.key === 'ArrowLeft')                           { e.preventDefault(); navigate(-1) }
  if (e.key === 'Home')                                { e.preventDefault(); viewer.jumpFirst(); _syncAfterJump() }
  if (e.key === 'End')                                 { e.preventDefault(); viewer.jumpLast();  _syncAfterJump() }

  // Zoom shortcuts
  viewer.handleKeydown(e)
}, { capture: true })

// ── Text select mode ─────────────────────────────────────────────────────

let _textSelectMode = false
const _copyTooltip = document.getElementById('copy-tooltip')

function _showCopyTooltip(x, y) {
  if (!_copyTooltip) return
  _copyTooltip.style.left = `${x + 8}px`
  _copyTooltip.style.top  = `${y - 36}px`
  _copyTooltip.style.display = 'block'
}
function _hideCopyTooltip() {
  if (_copyTooltip) _copyTooltip.style.display = 'none'
}

// Show Copy button when the user finishes a drag-select in text-select mode
document.addEventListener('mouseup', (e) => {
  if (!_textSelectMode) return
  requestAnimationFrame(() => {
    const sel = window.getSelection()
    if (sel && sel.toString().trim().length > 0) {
      _showCopyTooltip(e.clientX, e.clientY)
    } else {
      _hideCopyTooltip()
    }
  })
})

// ── Vertical (rotated) text: click to select whole span ──────────────────
// Browsers can't drag-select across CSS-transformed (rotated) spans.
// A single click on a rotated span selects the full span text instead.
function _isRotatedSpan(el) {
  if (!el || (el.tagName !== 'SPAN' && el.tagName !== 'BR')) return false
  if (!el.closest('.textLayer')) return false
  const t = window.getComputedStyle(el).transform
  if (!t || t === 'none') return false
  try {
    const m = new DOMMatrix(t)
    // b or c non-zero means rotation — threshold 0.2 (~12°)
    return Math.abs(m.b) > 0.2 || Math.abs(m.c) > 0.2
  } catch { return false }
}

document.addEventListener('click', (e) => {
  if (!_textSelectMode) return
  if (!_isRotatedSpan(e.target)) return
  // Select the entire rotated span's text content
  const range = document.createRange()
  range.selectNodeContents(e.target)
  const sel = window.getSelection()
  sel.removeAllRanges()
  sel.addRange(range)
  _showCopyTooltip(e.clientX, e.clientY)
})

// Hide when starting a new click
document.addEventListener('mousedown', (e) => {
  if (e.target === _copyTooltip) return
  _hideCopyTooltip()
})

// Copy button click
_copyTooltip?.addEventListener('click', async () => {
  const sel = window.getSelection()
  const text = sel?.toString() ?? ''
  if (!text) return
  try {
    await navigator.clipboard.writeText(text)
  } catch {
    // fallback for restricted contexts
    document.execCommand('copy')
  }
  _hideCopyTooltip()
  sel?.removeAllRanges()
})

// Ctrl+C also copies selected text explicitly
document.addEventListener('keydown', (e) => {
  if (!_textSelectMode) return
  if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
    const sel = window.getSelection()
    const text = sel?.toString() ?? ''
    if (!text) return
    navigator.clipboard.writeText(text).catch(() => document.execCommand('copy'))
    _hideCopyTooltip()
  }
}, { capture: true })

function _setTextSelectMode(on) {
  _textSelectMode = on
  viewer.setTextSelectMode(on)
  btnTextSelect?.classList.toggle('active', on)
  // mutually exclusive with draw mode (clears its tool-active state)
  if (on && _drawMode) _setDrawMode(false)
  // Text-select must NOT block panning: a left-drag on empty area still pans;
  // only a drag that starts on a text span selects. So it never marks a tool
  // active (doing so was what froze the plan in place once selected).
  viewer.setToolActive(_drawMode)
  // Leaving text-select must never strand a half-finished gesture or a lingering
  // DOM selection that makes pan/zoom feel locked.
  if (!on) viewer.resetInteraction()
  // Drop focus so the toggle button doesn't hold the ribbon open via :focus-within.
  btnTextSelect?.blur()
}

btnTextSelect?.addEventListener('click', () => {
  if (_pdf) _setTextSelectMode(!_textSelectMode)
})

// ── BBox draw mode ───────────────────────────────────────────────────────

let _drawMode = false
let _drawModeBeforeZoom = false
let _lastDrawnBbox = null  // { pageIdx, rect, imgBbox } - rect in PDF points, imgBbox in 150 DPI pixels

function _setDrawMode(on) {
  _drawMode = on
  overlay.setDrawMode(on)
  btnDrawBbox?.classList.toggle('active', on)
  viewer.setToolActive(on)
  if (on) {
    // Collapse the ribbon so it doesn't block the canvas while the user drags a
    // box (lets them draw from the very top of the page).
    document.body.classList.add('draw-mode-active')
  } else {
    // Drawing finished — always restore the ribbon so it's reachable again. It
    // stays auto-hidden and only reveals on hover, so it won't block the
    // floating Pull Table button on the canvas.
    document.body.classList.remove('draw-mode-active')
  }
  // mutually exclusive with text-select mode
  if (on && _textSelectMode) _setTextSelectMode(false)
}

function _updateExportButton() {}

btnDrawBbox?.addEventListener('click', () => {
  if (_pdf) _setDrawMode(!_drawMode)
})

/**
 * Called every time the user finishes drawing a bounding box.
 * `rect` is in PDF-point space at scale=1 (same as all other overlay rects).
 * Shift+drag triggers morphological line snapping before committing.
 */
async function _onBboxDrawn(rect, opts = {}) {
  // Always exit draw mode after one bbox is drawn
  _setDrawMode(false)

  // Use the viewer's actually-displayed page as the source of truth. The pure
  // pdf.js vector viewer owns `_currentPageIdx`; `state.currentPage` can lag
  // behind it (some navigation paths don't push it back into state), which
  // would otherwise send a stale page number to the backend.
  const pageIdx = viewer.currentPage()
  log(`BBox drawn on page ${pageIdx + 1}: x=${rect.x.toFixed(1)} y=${rect.y.toFixed(1)} w=${rect.w.toFixed(1)} h=${rect.h.toFixed(1)}`)

  // Convert PDF points to 150 DPI image coordinates for backend
  const pdfToImg = 150 / 72
  let imgBbox = [
    Math.round(rect.x * pdfToImg),
    Math.round(rect.y * pdfToImg),
    Math.round((rect.x + rect.w) * pdfToImg),
    Math.round((rect.y + rect.h) * pdfToImg),
  ]

  // Shift+drag: snap each edge to the nearest detected ruling line
  if (opts.shiftSnap) {
    const docId = state.getState().docId
    if (docId) {
      try {
        log('Shift held — snapping bbox to ruling lines…')
        const snapResp = await fetch('/api/detection/detect-lines', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ doc_id: docId, page_index: pageIdx, img_bbox: imgBbox, margin: 80 }),
        })
        if (snapResp.ok) {
          const { h_lines, v_lines } = await snapResp.json()
          imgBbox = _snapBbox(imgBbox, h_lines, v_lines, 40)
          rect = {
            x: imgBbox[0] / pdfToImg,
            y: imgBbox[1] / pdfToImg,
            w: (imgBbox[2] - imgBbox[0]) / pdfToImg,
            h: (imgBbox[3] - imgBbox[1]) / pdfToImg,
          }
          overlay.setActiveBbox(rect)
          log(`Snapped bbox: [${imgBbox.join(', ')}]`)
        }
      } catch (snapErr) {
        console.warn('Snap failed:', snapErr)
      }
    }
  }

  _lastDrawnBbox = { pageIdx, rect, imgBbox }
  log(`Ready to export: imgBbox=[${imgBbox.join(', ')}]`)
}

/**
 * Snap each edge of imgBbox to the nearest line within threshPx.
 * imgBbox = [x1, y1, x2, y2] in image pixels.
 */
function _snapBbox(imgBbox, hLines, vLines, threshPx = 40) {
  const snap = (val, lines) => {
    if (!lines || !lines.length) return val
    const nearest = lines.reduce((a, b) => Math.abs(b - val) < Math.abs(a - val) ? b : a)
    return Math.abs(nearest - val) <= threshPx ? nearest : val
  }
  return [
    snap(imgBbox[0], vLines),
    snap(imgBbox[1], hLines),
    snap(imgBbox[2], vLines),
    snap(imgBbox[3], hLines),
  ]
}

/**
 * Export the last drawn bbox as an Excel table.
 */
async function _exportTable() {
  if (!_lastDrawnBbox) {
    log('No bbox to export')
    return
  }

  const { pageIdx, imgBbox } = _lastDrawnBbox
  // Prefer the shared state docId, but fall back to the active document tab's
  // docId — the shared state can be cleared when switching to/from callout tabs
  // even though the cached job's docId is still valid on the tab.
  const activeTab = _tabs.find(t => t.id === _activeTabId && t.type === 'document')
  const docId = state.getState().docId || activeTab?.docId || null

  if (!docId) {
    log('Document still uploading to backend — please wait a moment and try again')
    return
  }

  log(`Exporting table from page ${pageIdx + 1}...`)

  // If the GPU worker is asleep, warn the user and keep them posted while the
  // export request itself boots the box. The request blocks until the worker
  // wakes (~1 min) and finishes, so the table still comes through.
  let statusPoll = null
  try {
    const st = await api.workerStatus()
    if (st && st.enabled && st.state !== 'running') {
      log('⏳ The extraction service was asleep — starting the GPU worker now. This usually takes about a minute, then the table exports automatically. Please keep this tab open.')
      let lastState = st.state
      statusPoll = setInterval(async () => {
        try {
          const s = await api.workerStatus()
          if (!s || !s.enabled || s.state === lastState) return
          lastState = s.state
          if (s.state === 'running') log('GPU worker is up — extracting the table now…')
          else if (s.state === 'loading' || s.state === 'starting') log('GPU worker is booting…')
        } catch { /* keep polling */ }
      }, 5000)
    }
  } catch { /* status is best-effort */ }

  try {
    // Step 1: POST triggers detection + export + debug image. Returns JSON with URLs.
    const response = await fetch('/api/detection/export', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        doc_id: docId,
        page: pageIdx,
        bbox: imgBbox,
        format: 'xlsx',
      }),
    })

    if (!response.ok) {
      let msg = `HTTP ${response.status}`
      try {
        const body = await response.json()
        if (body && body.detail) msg = body.detail
      } catch {
        const txt = await response.text().catch(() => '')
        if (txt) msg = txt
      }
      throw new Error(msg)
    }

    await response.json().catch(() => ({}))

    // The table is now persisted WITH the project — reload the saved list.
    await _loadSavedTables(docId, { expand: true })

    // Clear the drawn bbox now that the table is captured
    overlay.clearActiveBbox()
    _lastDrawnBbox = null

    log('Table saved to this project \u2014 see "Saved Tables" in the left panel.')
  } catch (err) {
    const m = String(err.message || '')
    if (statusPoll && /timed out|timeout|502|503|504/i.test(m)) {
      log('The GPU worker is still waking up. Give it a few more seconds, then click Pull Table again.')
    } else {
      log(`Export failed: ${err.message}`)
    }
    console.error('Export error:', err)
  } finally {
    if (statusPoll) clearInterval(statusPoll)
  }
}

// ── Saved Tables — persisted per project (docs/<id>/tables in R2) ─────────────
let _savedTablesDocId = null

async function _loadSavedTables(docId, opts = {}) {
  const section = document.getElementById('exports-section')
  if (!section) return
  _savedTablesDocId = docId || null
  if (!docId) { section.classList.add('hidden'); return }
  try {
    const { tables } = await api.listSavedTables(docId)
    _renderSavedTables(docId, tables || [], opts)
  } catch { /* a failed fetch shouldn't blank the panel */ }
}

function _renderSavedTables(docId, tables, opts = {}) {
  const section = document.getElementById('exports-section')
  const listEl  = document.getElementById('exports-list')
  const countEl = document.getElementById('exports-count')
  if (!section || !listEl) return

  if (!tables.length) {
    listEl.innerHTML = '<p class="meta-empty">No saved tables</p>'
    if (countEl) countEl.textContent = ''
    section.classList.add('hidden')
    return
  }

  section.classList.remove('hidden')
  panelCategories.classList.remove('hidden')
  if (opts.expand) section.classList.add('expanded')
  if (countEl) countEl.textContent = String(tables.length)

  listEl.innerHTML = ''
  for (const t of tables) {
    const item = document.createElement('div')
    item.className = 'export-item'

    const cb = document.createElement('input')
    cb.type = 'checkbox'
    cb.className = 'export-check'
    cb.dataset.exportId = t.export_id
    cb.dataset.pageNum = t.page_num
    cb.title = 'Select for Combine'

    const link = document.createElement('a')
    link.href = '#'
    link.className = 'export-link'
    link.textContent = (t.filename || `Page ${t.page_num}`).replace(/\.(xlsx|csv)$/i, '')
    link.title = 'Open this table in chat'
    link.addEventListener('click', (e) => { e.preventDefault(); _openSavedTableInChat(docId, t) })

    const dl = document.createElement('button')
    dl.className = 'export-dl'
    dl.title = 'Download Excel'
    dl.textContent = '\u2193'
    dl.addEventListener('click', (e) => { e.stopPropagation(); _downloadSavedTable(docId, t) })

    const del = document.createElement('button')
    del.className = 'export-dismiss'
    del.title = 'Remove from project'
    del.innerHTML = '&times;'
    del.addEventListener('click', async (e) => {
      e.stopPropagation()
      // Confirm — a stray click here permanently removes the saved table.
      if (!window.confirm(`Remove the saved table "${t.filename || 'Page ' + t.page_num}" from this project? This can't be undone.`)) return
      try { await api.deleteSavedTable(docId, t.export_id) } catch {}
      await _loadSavedTables(docId)
    })

    item.appendChild(cb)
    item.appendChild(link)
    item.appendChild(dl)
    item.appendChild(del)
    listEl.appendChild(item)
  }
}

// Clicking a saved table bottom-left re-opens it in chat as a LIVE editable table.
async function _openSavedTableInChat(docId, t) {
  try {
    const cells = await api.getLiveTableCells(docId, t.export_id)
    openLiveTable({ ...cells, export_id: t.export_id })
  } catch (e) {
    log(`Could not open table in chat: ${e.message}`)
  }
}

async function _downloadSavedTable(docId, t) {
  try {
    const resp = await fetch(`/api/detection/download/${docId}/${t.export_id}`)
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
    const blob = await resp.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    const baseName = _fileName ? _fileName.replace(/\.(pdf|bpx)$/i, '') + '_' : ''
    a.href = url
    a.download = t.filename || `${baseName}page_${t.page_num}_table.xlsx`
    document.body.appendChild(a); a.click(); a.remove()
    URL.revokeObjectURL(url)
  } catch (err) {
    log(`Download failed: ${err.message}`)
  }
}

// Header toggles the dropdown; the Combine button is handled separately.
document.getElementById('exports-section-header')?.addEventListener('click', (e) => {
  if (e.target.closest('#btn-combine-all')) return
  document.getElementById('exports-section')?.classList.toggle('expanded')
})

// ── Combine tables — the CHECKED ones (targeted), else all saved tables ───────
document.getElementById('btn-combine-all').addEventListener('click', async () => {
  const docId = _savedTablesDocId
  if (!docId) { log('Open a plan with saved tables first'); return }
  const checks = [...document.querySelectorAll('#exports-list .export-check')]
  const selected = checks.filter(c => c.checked)
  const use = (selected.length ? selected : checks).map(c => ({
    doc_id: docId,
    export_id: c.dataset.exportId,
    page_num: parseInt(c.dataset.pageNum, 10) || 0,
  }))
  if (!use.length) { log('No saved tables to combine — export at least one first'); return }
  use.sort((a, b) => a.page_num - b.page_num)
  const pageList = use.map(e => `p${e.page_num}`).join(', ')
  log(`Combining ${use.length} table(s): ${pageList}\u2026`)
  try {
    const resp = await fetch('/api/detection/combine', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ exports: use }),
    })
    if (!resp.ok) throw new Error((await resp.text()) || `HTTP ${resp.status}`)
    const blob = await resp.blob()
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    const combinedBase = _fileName ? _fileName.replace(/\.(pdf|bpx)$/i, '') + '_' : ''
    a.href = url
    a.download = `${combinedBase}combined_tables.xlsx`
    document.body.appendChild(a); a.click(); a.remove()
    log(`Combined workbook downloaded \u2014 ${use.length} sheet(s) (${pageList})`)
  } catch (err) {
    log(`Combine failed: ${err.message}`)
    console.error('Combine error:', err)
  }
})

function _syncAfterJump() {
  const cp = viewer.currentPage()
  state.update({ currentPage: cp })
  _updatePageLabel()
  _updateNavButtons()
  _loadPageData(cp)
  _refreshSearchHighlightsForPage(cp)
  _updateActiveStripThumb(cp)
}

// ── Classification / Page Organization ────────────────────────────────────

/**
 * Run light-graph preprocessing for a document, then load its page
 * classification (category bins) and table_callout groups (callout view).
 * Shows a progress bar in the category panel while the builder runs.
 */
async function _runPreprocessing(docId) {
  setProjectProcessing(docId, true)   // lock this plan's chat until CatBoost/YOLO finish
  const showUI = _isDocActive(docId)
  if (showUI) {
    panelCategories.classList.remove('hidden')
    categoriesList.innerHTML = '<p class="meta-empty">Preprocessing\u2026</p>'
    if (classifyProgressBar) classifyProgressBar.style.display = 'block'
    if (classifyBar) classifyBar.style.width = '0%'
    if (classifyStatus) classifyStatus.textContent = 'Preprocessing\u2026'
  }

  try {
    await api.preprocessDocument(docId, {
      onStatus: (s) => {
        if (classifyStatus) classifyStatus.textContent = s.message || 'Working\u2026'
      },
      onProgress: (p) => {
        const pct = p.total ? Math.round((p.done / p.total) * 100) : 0
        if (classifyBar) classifyBar.style.width = `${pct}%`
        if (classifyStatus) classifyStatus.textContent = `Processing\u2026 ${p.done} / ${p.total} pages`
      },
    })

    if (classifyBar) classifyBar.style.width = '100%'
    if (classifyStatus) classifyStatus.textContent = 'Loading results\u2026'

    // Page classification → category bins
    const result = await api.getPreprocessClassification(docId)
    const categoryMap = {}
    for (const page of result.pages) {
      for (const cat of page.categories) {
        if (!categoryMap[cat]) categoryMap[cat] = []
        categoryMap[cat].push(page.page)
      }
    }
    const classifyResults = { pages: result.pages, categoryMap, total: result.total }

    // Table callouts → callout view
    let callouts = []
    try {
      const cres = await api.getCallouts(docId)
      callouts = cres.callouts || []
    } catch {
      callouts = []
    }

    // Persist onto the owning plan tab so its side menu survives tab switches —
    // even if this finished while the user was reading a supporting document.
    const ownerTab = _tabs.find(t => t.type === 'document' && t.docId === docId && !t.viewOnly)
    if (ownerTab && ownerTab.appState) {
      ownerTab.appState.classifyResults = classifyResults
      ownerTab.appState.callouts = callouts
    }

    const active = _isDocActive(docId)
    if (active) {
      _classifyResults = classifyResults
      _callouts = callouts
      if (classifyStatus) {
        classifyStatus.textContent =
          `${result.pages.length} pages \u00b7 ${callouts.length} callouts`
      }
    }

    setTimeout(() => {
      if (active && classifyProgressBar) classifyProgressBar.style.display = 'none'
      if (active) _renderOrgView()
    }, 400)

    // Mark the job as fully cached so the recent-jobs menu shows it as ready.
    api.markDocumentReady(docId)
    setProjectProcessing(docId, false)   // plan is ready — unlock its chat
    _refreshRecentJobsSoon()

    log(`Preprocessing complete: ${Object.keys(categoryMap).length} categories, ` +
        `${callouts.length} callouts, ${result.total} pages`)

  } catch (err) {
    setProjectProcessing(docId, false)   // don't strand the chat locked on failure
    if (classifyStatus) classifyStatus.textContent = `Error: ${err.message}`
    categoriesList.innerHTML = `<p class="meta-empty">Preprocessing failed</p>`
    log(`Preprocessing failed: ${err.message}`)
    setTimeout(() => {
      if (classifyProgressBar) classifyProgressBar.style.display = 'none'
    }, 2500)
  }
}

/** Render whichever left-panel organization view is active. */
function _renderOrgView() {
  const mode = _orgView
  orgViewCategory?.classList.toggle('active', mode === 'category')
  orgViewCallout?.classList.toggle('active', mode === 'callout')
  orgViewDocument?.classList.toggle('active', mode === 'document')

  categoriesList.classList.toggle('hidden', mode !== 'category')
  calloutsList.classList.toggle('hidden', mode !== 'callout')
  documentsList?.classList.toggle('hidden', mode !== 'document')

  if (mode === 'callout') _renderCalloutList()
  else if (mode === 'document') _renderDocumentPackage()
  else _renderCategoryBins()
}

/** The doc id of the currently active project tab. */
function _activeDocId() {
  const tab = _tabs.find(t => t.id === _activeTabId)
  return state.getState().docId || tab?.docId || null
}

/** True when the plan tab that owns docId is the one currently on screen. */
function _isDocActive(docId) {
  if (!docId) return false
  const tab = _tabs.find(t => t.id === _activeTabId)
  return !!tab && !tab.viewOnly && tab.docId === docId
}

/** Prompt for a PDF via a transient file input. Resolves File|null. */
function _pickPdf() {
  return new Promise((resolve) => {
    const inp = document.createElement('input')
    inp.type = 'file'
    inp.accept = 'application/pdf,.pdf'
    inp.addEventListener('change', () => resolve(inp.files?.[0] || null), { once: true })
    inp.click()
  })
}

/** "By Document" view: the plan plus its attached proposals/addendums + specs. */
async function _renderDocumentPackage() {
  if (!documentsList) return
  const docId = _activeDocId()
  if (!docId) { documentsList.innerHTML = '<p class="meta-empty">Open a plan to manage its documents</p>'; return }
  documentsList.innerHTML = '<p class="meta-empty">Loading\u2026</p>'
  let pkg, specs = []
  try {
    const [p, s] = await Promise.all([
      api.getProjectPackage(docId),
      api.listSpecs().catch(() => ({ specs: [] })),
    ])
    pkg = p; specs = s?.specs || []
  } catch { documentsList.innerHTML = '<p class="meta-empty">Could not load documents</p>'; return }
  if (_activeDocId() !== docId) return   // user switched tabs while loading
  _buildPackageUI(docId, pkg, specs)
}

function _buildPackageUI(docId, pkg, specs) {
  documentsList.innerHTML = ''
  const items = Array.isArray(pkg?.items) ? pkg.items : []

  const planRow = document.createElement('div')
  planRow.className = 'pkg-plan'
  planRow.innerHTML = `<span class="pkg-plan-name" title="${pkg?.plan?.filename || ''}">${pkg?.plan?.filename || 'Plan'}</span>
    <span class="pkg-pages">${pkg?.plan?.page_count ? pkg.plan.page_count + ' pg' : ''}</span>`
  documentsList.appendChild(planRow)

  for (const kind of ['proposal', 'addendum']) {
    const label = kind === 'proposal' ? 'Proposals' : 'Addendums'
    const group = document.createElement('div')
    group.className = 'pkg-group'
    const head = document.createElement('div')
    head.className = 'pkg-group-head'
    head.innerHTML = `<span>${label}</span>`
    const addBtn = document.createElement('button')
    addBtn.className = 'pkg-add'
    addBtn.textContent = '+ Add'
    addBtn.title = `Add a ${kind}`
    addBtn.addEventListener('click', () => _addAttachment(docId, kind))
    head.appendChild(addBtn)
    group.appendChild(head)

    const rows = items.filter(it => it.kind === kind)
    if (!rows.length) {
      const empty = document.createElement('p')
      empty.className = 'meta-empty pkg-empty'
      empty.textContent = `No ${label.toLowerCase()} yet`
      group.appendChild(empty)
    }
    for (const it of rows) group.appendChild(_attachmentRow(docId, it))
    documentsList.appendChild(group)
  }

  // Specifications — global, shared across every plan (open/read-only here).
  const specGroup = document.createElement('div')
  specGroup.className = 'pkg-group'
  const sh = document.createElement('div')
  sh.className = 'pkg-group-head'
  sh.innerHTML = '<span>Specifications</span><span class="pkg-shared">shared</span>'
  specGroup.appendChild(sh)
  if (!(specs && specs.length)) {
    const e = document.createElement('p'); e.className = 'meta-empty pkg-empty'; e.textContent = 'No specifications'
    specGroup.appendChild(e)
  }
  for (const s of (specs || [])) specGroup.appendChild(_specRow(s))
  documentsList.appendChild(specGroup)
}

function _attachmentRow(docId, it) {
  const row = document.createElement('div')
  row.className = 'pkg-item pkg-item-clickable'
  const name = document.createElement('span')
  name.className = 'pkg-item-name'
  name.textContent = it.filename || it.att_id
  name.title = `Open ${it.filename || ''}`
  const pages = document.createElement('span')
  pages.className = 'pkg-pages'
  pages.textContent = it.pages ? `${it.pages} pg` : ''
  const actions = document.createElement('span')
  actions.className = 'pkg-actions'
  const repB = document.createElement('button'); repB.textContent = 'Replace'; repB.title = 'Swap the file'
  repB.addEventListener('click', (e) => { e.stopPropagation(); _replaceAttachment(docId, it) })
  const delB = document.createElement('button'); delB.textContent = '\u2715'; delB.className = 'pkg-del'; delB.title = 'Remove'
  delB.addEventListener('click', (e) => { e.stopPropagation(); _removeAttachment(docId, it) })
  actions.append(repB, delB)
  row.append(name, pages, actions)
  row.addEventListener('click', () => _openAttachment(docId, it))
  return row
}

function _specRow(s) {
  const row = document.createElement('div')
  row.className = 'pkg-item pkg-item-clickable'
  const name = document.createElement('span')
  name.className = 'pkg-item-name'
  name.textContent = s.filename || s.spec_id
  name.title = `Open ${s.filename || ''}`
  const pages = document.createElement('span')
  pages.className = 'pkg-pages'
  pages.textContent = s.pages ? `${s.pages} pg` : ''
  row.append(name, pages)
  row.addEventListener('click', () => _openSpec(s.spec_id, s.filename))
  return row
}

async function _addAttachment(docId, kind) {
  const file = await _pickPdf()
  if (!file) return
  try { await api.addProjectAttachment(docId, file, kind); log(`Added ${kind}: ${file.name}`) }
  catch (e) { log(`Could not add ${kind}: ${e.message}`) }
  if (_activeDocId() === docId) _renderDocumentPackage()
}

async function _replaceAttachment(docId, it) {
  const file = await _pickPdf()
  if (!file) return
  try { await api.replaceProjectAttachment(docId, it.att_id, file); log(`Replaced ${it.filename || 'document'}`) }
  catch (e) { log(`Could not replace: ${e.message}`) }
  if (_activeDocId() === docId) _renderDocumentPackage()
}

async function _removeAttachment(docId, it) {
  if (!window.confirm(`Remove "${it.filename || 'this document'}" from the project?`)) return
  try { await api.removeProjectAttachment(docId, it.att_id); log(`Removed ${it.filename || 'document'}`) }
  catch (e) { log(`Could not remove: ${e.message}`) }
  if (_activeDocId() === docId) _renderDocumentPackage()
}

async function _openAttachment(docId, it) {
  try {
    const planTab = _tabs.find(t => t.id === _activeTabId)
    const chatProject = {
      docId,
      label: planTab?.label || 'Plan',
      viewing: { kind: it.kind || 'proposal', filename: it.filename || it.att_id, id: it.att_id },
    }
    log(`Opening ${it.filename || 'document'}\u2026`)
    const file = await api.getProjectAttachmentFile(docId, it.att_id, it.filename)
    await _openPdfInNewTab(file, true, null, chatProject, true)   // view-only tab bound to the plan's chat
  } catch (e) { log(`Could not open: ${e.message}`) }
}

async function _openSpec(specId, filename) {
  try {
    const planId = _activeDocId()
    const planTab = _tabs.find(t => t.id === _activeTabId)
    const chatProject = planId ? {
      docId: planId,
      label: planTab?.label || 'Plan',
      viewing: { kind: 'spec', filename: filename || specId, id: specId },
    } : null
    log(`Opening ${filename || 'spec'}\u2026`)
    const file = await api.getSpecFile(specId, filename)
    await _openPdfInNewTab(file, true, null, chatProject, true)   // view-only; keeps the plan's project chat
  } catch (e) { log(`Could not open: ${e.message}`) }
}

/** Render the list of table callouts, grouped by their letter prefix
 *  (D, E, F…). Each prefix is a collapsible dropdown of its callouts. */
function _renderCalloutList() {
  calloutsList.innerHTML = ''
  if (!_callouts || _callouts.length === 0) {
    calloutsList.innerHTML = '<p class="meta-empty">No callouts found</p>'
    return
  }

  // Group by leading-letter prefix (e.g. "D1" → "D", "CR-2" → "CR").
  const groups = new Map()
  for (const c of _callouts) {
    const m = /^([A-Za-z]+)/.exec(c.text.trim())
    const prefix = m ? m[1].toUpperCase() : '#'
    if (!groups.has(prefix)) groups.set(prefix, [])
    groups.get(prefix).push(c)
  }

  for (const [prefix, items] of groups) {
    const group = document.createElement('div')
    group.className = 'callout-group'

    const header = document.createElement('div')
    header.className = 'callout-group-header'
    const totalPages = new Set(items.flatMap(c => c.pages)).size
    header.innerHTML =
      `<span class="callout-group-caret">\u25b6</span>` +
      `<span class="callout-group-name">${prefix}</span>` +
      `<span class="callout-group-meta">${items.length} callout(s) \u00b7 ${totalPages} pg</span>`

    const body = document.createElement('div')
    body.className = 'callout-group-body'

    for (const c of items) {
      const item = document.createElement('div')
      item.className = 'callout-item'
      item.title = `${c.text} \u2014 ${c.pages.length} page(s), ${c.count} instance(s)`

      const name = document.createElement('span')
      name.className = 'callout-name'
      name.textContent = c.text

      const meta = document.createElement('span')
      meta.className = 'callout-meta'
      meta.textContent = `${c.pages.length} pg`

      const count = document.createElement('span')
      count.className = 'callout-count'
      count.textContent = String(c.count)

      item.appendChild(name)
      item.appendChild(meta)
      item.appendChild(count)
      item.addEventListener('click', () => _openCalloutTab(c))
      body.appendChild(item)
    }

    header.addEventListener('click', () => group.classList.toggle('expanded'))
    group.appendChild(header)
    group.appendChild(body)
    calloutsList.appendChild(group)
  }
}

// Resolve a citation against the user's actual documents before navigating.
// A source removed since the answer was saved never falls back to the active PDF.
async function _goToCitedPage(ref) {
  try {
    if (!ref || !Number.isSafeInteger(ref.page) || ref.page < 1) return
    let tabId = null
    if (ref.kind === 'plan') {
      const doc = await api.getDocument(ref.id) // backend enforces ownership
      if (ref.page > doc.page_count) throw new Error('Page is outside this plan')
      await _openDocById(ref.id, doc.filename)
      tabId = _findOpenDocumentTabId(ref.id)
    } else if (ref.kind === 'attachment') {
      const pkg = await api.getProjectPackage(ref.projectId)
      const item = (pkg.items || []).find(it => it.att_id === ref.id)
      if (!item || ref.page > item.pages) throw new Error('Attachment or page is unavailable')
      const existing = _tabs.find(t => t.viewOnly && t.chatProjectId === ref.projectId && t.viewDoc?.id === ref.id && t.viewDoc?.kind === item.kind)
      if (existing) tabId = existing.id
      else {
        const file = await api.getProjectAttachmentFile(ref.projectId, ref.id, item.filename)
        tabId = await _openPdfInNewTab(file, true, null, {
          docId: ref.projectId, label: pkg.plan?.filename || 'Plan',
          viewing: { kind: item.kind, filename: item.filename, id: ref.id },
        }, true)
      }
    } else if (ref.kind === 'spec') {
      const { specs } = await api.listSpecs()
      const spec = (specs || []).find(s => s.spec_id === ref.id)
      if (!spec || ref.page > spec.pages) throw new Error('Specification or page is unavailable')
      const activeTab = _tabs.find(t => t.id === _activeTabId)
      const projectId = _activeDocId() || activeTab?.chatProjectId || null
      const existing = _tabs.find(t => t.viewOnly && t.viewDoc?.kind === 'spec' &&
        t.viewDoc?.id === ref.id && (t.chatProjectId || null) === projectId)
      if (existing) tabId = existing.id
      else {
        const file = await api.getSpecFile(ref.id, spec.filename)
        tabId = await _openPdfInNewTab(file, true, null, projectId ? {
          docId: projectId, label: 'Plan', viewing: { kind: 'spec', filename: spec.filename, id: ref.id },
        } : null, true)
      }
    } else return
    if (!tabId) throw new Error('Document could not be opened')
    await _switchToTab(tabId)
    if (_activeTabId !== tabId || !_pdf || ref.page > _pdf.numPages) throw new Error('Page is outside the opened PDF')
    const idx = ref.page - 1
    state.update({ currentPage: idx })
    // Navigate LAST and AWAIT it. A freshly opened/restored tab settles its own
    // first-page render (page 1) which can otherwise land AFTER this nav and
    // strand the user there; re-assert on the next frame so the cited page is
    // the one shown (the second call is a cheap re-center if it already did).
    await viewer.goToPage(idx)
    await new Promise(r => requestAnimationFrame(r))
    await viewer.goToPage(idx)
    state.update({ currentPage: idx })
    _updatePageLabel()
    _updateNavButtons()
  } catch (err) {
    log(`Could not open citation: ${err.message}`)
    window.alert(`Could not open cited page: ${err.message}`)
  }
}

/**
 * Open a new tab showing every page that references a callout in one
 * zoomable contact-sheet view, each page with the callout highlighted.
 */
function _openCalloutTab(callout) {
  if (!_pdf) return
  _saveCurrentTab()
  const id = _nextTabId()
  /** @type {Tab} */
  const tab = {
    id,
    label: `\u25c9 ${callout.text}`,
    type: 'callout',
    file: null,
    docId: _activeDocId(),
    // Snapshot the rendered manifest NOW (before viewer.detach() clears it)
    // so callout pages can load previews without the live viewer being active.
    renderedManifest: viewer.getRenderedManifest() || null,
    sharedPdf: true,
    pdf: _pdf,
    fileName: _fileName,
    callout,
    calloutZoom: 1.0,
    viewerState: null,
    appState: null,
    pages: null,
  }
  _tabs.push(tab)
  _activeTabId = id
  _renderTabBar()
  _activateTab(id)

  log(`Callout "${callout.text}" \u2014 ${callout.pages.length} page(s), ${callout.count} instance(s)`)
}

function _renderCategoryBins() {
  // Preserve which accordions are open so incremental re-renders don't collapse them
  const expandedCats = new Set()
  categoriesList.querySelectorAll('.category-bin.expanded .cat-name').forEach(el => {
    expandedCats.add(el.textContent)
  })

  categoriesList.innerHTML = ''
  if (!_classifyResults) return

  const { categoryMap } = _classifyResults

  // Sort categories by construction document reading order
  const orderIndex = (cat) => {
    const idx = CATEGORY_ORDER.indexOf(cat)
    return idx >= 0 ? idx : CATEGORY_ORDER.length - 1
  }
  const cats = Object.keys(categoryMap).sort((a, b) => orderIndex(a) - orderIndex(b))

  for (const cat of cats) {
    const pages = categoryMap[cat].sort((a, b) => a - b)
    const isExpanded = expandedCats.has(cat)
    const bin = document.createElement('div')
    bin.className = 'category-bin' +
      (cat === 'UNORGANIZED' ? ' unorganized-bin' : '') +
      (isExpanded ? ' expanded' : '')

    const header = document.createElement('div')
    header.className = 'cat-header'
    header.innerHTML = `
      <span class="cat-arrow">${isExpanded ? '&#9660;' : '&#9654;'}</span>
      <span class="cat-name">${cat}</span>
      <span class="cat-count">${pages.length}</span>
    `

    const thumbGrid = document.createElement('div')
    thumbGrid.className = 'cat-thumb-grid'

    // Re-populate thumbnails for any accordion that was open before the rebuild
    if (isExpanded) {
      _renderCategoryThumbnails(thumbGrid, pages)
    }

    header.addEventListener('click', () => {
      const isOpen = bin.classList.contains('expanded')
      if (isOpen) {
        bin.classList.remove('expanded')
        header.querySelector('.cat-arrow').innerHTML = '&#9654;'
      } else {
        bin.classList.add('expanded')
        header.querySelector('.cat-arrow').innerHTML = '&#9660;'
        if (thumbGrid.children.length === 0) {
          _renderCategoryThumbnails(thumbGrid, pages)
        }
      }
    })

    bin.appendChild(header)
    bin.appendChild(thumbGrid)
    categoriesList.appendChild(bin)
  }
}

// ── Thumbnail worker pool ─────────────────────────────────────────────────────
// Three independent PDFDocumentProxy instances = three PDF.js workers.
// Renders are distributed round-robin so all three workers run in parallel,
// giving ~3× thumbnail throughput without touching the main viewer's worker.
const _POOL_SIZE  = 3
const _THUMB_RENDER_CONCURRENCY = 2
let   _thumbPool  = []   // PDFDocumentProxy[]
let   _thumbRR    = 0    // round-robin cursor
let   _poolReady  = false
let   _poolPromise = null
let   _thumbQueue = []
let   _thumbActive = 0
let   _thumbQueueGeneration = 0

async function _initThumbPool() {
  if (_poolReady) return
  if (_poolPromise) return _poolPromise
  _poolPromise = (async () => {
    const docs = await Promise.all(
      Array.from({ length: _POOL_SIZE }, () => viewer.createThumbDoc())
    )
    _thumbPool = docs.filter(Boolean)
    _poolReady = _thumbPool.length > 0
  })()
  return _poolPromise
}

function _nextThumbDoc() {
  if (!_thumbPool.length) return viewer.getPdf()
  const doc = _thumbPool[_thumbRR % _thumbPool.length]
  _thumbRR++
  return doc
}

function _clearThumbPdf() {
  _clearQueuedThumbs()
  _poolReady  = false
  _poolPromise = null
  _thumbRR    = 0
  for (const doc of _thumbPool) {
    if (doc && doc !== viewer.getPdf()) try { doc.destroy() } catch {}
  }
  _thumbPool = []
}

function _queueThumbRender(renderFn, priority = false) {
  const generation = _thumbQueueGeneration
  const job = { generation, renderFn }
  if (priority) _thumbQueue.unshift(job)
  else _thumbQueue.push(job)
  _pumpThumbQueue()
}

function _pumpThumbQueue() {
  while (_thumbActive < _THUMB_RENDER_CONCURRENCY && _thumbQueue.length > 0) {
    const job = _thumbQueue.shift()
    _thumbActive += 1
    Promise.resolve()
      .then(() => {
        if (job.generation !== _thumbQueueGeneration) return null
        return job.renderFn()
      })
      .catch(() => {})
      .finally(() => {
        _thumbActive = Math.max(0, _thumbActive - 1)
        _pumpThumbQueue()
      })
  }
}

function _clearQueuedThumbs() {
  _thumbQueueGeneration += 1
  _thumbQueue = []
}

async function _renderOneThumb(canvas, pageIdx, widthPx, dpr = 1) {
  if (viewer.isRenderedMode()) {
    await viewer.renderRenderedAsset(canvas, pageIdx, widthPx, 'thumb', dpr)
    return
  }
  // Cached plans display as pure-vector 'pdf' mode but still carry the rendered manifest —
  // use the fast server WebP thumbnails for the page strip instead of re-rendering each
  // tiny image through pdf.js (which was slow).
  const manifest = viewer.getRenderedManifest()
  if (manifest) {
    const ok = await viewer.renderRenderedThumbFromManifest(canvas, manifest, pageIdx, widthPx, dpr)
    if (ok) return
  }
  if (!_poolReady) await _initThumbPool()
  try {
    const doc   = _nextThumbDoc()
    const page  = await doc.getPage(pageIdx + 1)
    const vp    = page.getViewport({ scale: 1 })
    const scale = (widthPx * dpr) / vp.width
    const tv    = page.getViewport({ scale })
    canvas.width  = Math.round(tv.width)
    canvas.height = Math.round(tv.height)
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    await page.render({ canvasContext: ctx, viewport: tv }).promise
  } catch { /* ignore */ }
}

/**
 * Render category thumbnails lazily (IntersectionObserver) using the pool.
 * Only visible thumbnails render; scrolling in reveals more.
 */
function _renderCategoryThumbnails(container, pageIndices) {
  const CAT_THUMB_W = 100

  for (const pageIdx of pageIndices) {
    const item = document.createElement('div')
    item.className = 'cat-thumb-item'
    item.title = `Page ${pageIdx + 1}`
    item.dataset.pageIdx = pageIdx

    const canvas = document.createElement('canvas')
    canvas.className = 'cat-thumb-canvas'

    const label = document.createElement('span')
    label.className = 'cat-thumb-label'
    label.textContent = `p${pageIdx + 1}`

    item.appendChild(canvas)
    item.appendChild(label)
    container.appendChild(item)

    item.addEventListener('click', async () => {
      viewer.setPageFilter(null)
      await viewer.goToPage(pageIdx)
      _syncAfterJump()
    })
  }

  const obs = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue
      obs.unobserve(entry.target)
      const idx    = parseInt(entry.target.dataset.pageIdx)
      const canvas = entry.target.querySelector('.cat-thumb-canvas')
      if (!isNaN(idx) && canvas) _queueThumbRender(() => _renderOneThumb(canvas, idx, CAT_THUMB_W))
    }
  }, { rootMargin: '300px' })

  for (const item of container.querySelectorAll('.cat-thumb-item')) {
    obs.observe(item)
  }
}

// ── Panel resize handle ───────────────────────────────────────────────────
if (panelResizeHandle && panelCategories) {
  let _resizing = false
  let _startX = 0
  let _startW = 0

  panelResizeHandle.addEventListener('mousedown', (e) => {
    e.preventDefault()
    _resizing = true
    _startX = e.clientX
    _startW = panelCategories.offsetWidth
    panelResizeHandle.classList.add('active')
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  })

  document.addEventListener('mousemove', (e) => {
    if (!_resizing) return
    const dx = e.clientX - _startX
    const newW = Math.max(180, Math.min(window.innerWidth * 0.6, _startW + dx))
    panelCategories.style.width = `${newW}px`
  })

  document.addEventListener('mouseup', () => {
    if (!_resizing) return
    _resizing = false
    panelResizeHandle.classList.remove('active')
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  })
}

// ── Page strip — skinny thumbnail navigator ───────────────────────────────

const pageStrip     = document.getElementById('page-strip')
const pageStripList = document.getElementById('page-strip-list')

// ── Callout contact-sheet view ────────────────────────────────────────────

const calloutView       = document.getElementById('callout-view')
const calloutViewGrid   = document.getElementById('callout-view-grid')
const calloutViewScroll = document.getElementById('callout-view-scroll')
const calloutViewTitle  = document.getElementById('callout-view-title')
const calloutZoomLabel  = document.getElementById('callout-zoom-label')
const calloutDownloadBtn = document.getElementById('callout-download')

calloutDownloadBtn?.addEventListener('click', async () => {
  const tab = _tabs.find(t => t.id === _activeTabId)
  if (!tab || tab.type !== 'callout' || !tab.docId || !tab.callout) return
  const pageIndices = [...new Set(tab.callout.pages)].sort((a, b) => a - b)
  if (!pageIndices.length) return
  // Sanitise callout text for use as a filename
  const safeName = (tab.callout.text || 'callout')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .slice(0, 120)
  calloutDownloadBtn.disabled = true
  calloutDownloadBtn.textContent = 'Preparing\u2026'
  try {
    const blob = await api.downloadPagesSubset(tab.docId, pageIndices, safeName)
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = safeName + '.pdf'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } catch (err) {
    log('Download failed: ' + err)
    alert('Download failed — see console for details.')
  } finally {
    calloutDownloadBtn.disabled = false
    calloutDownloadBtn.textContent = '\u2B07 Download Pages'
  }
})

const CALLOUT_BASE_W   = 340    // CSS display width per page at 100% zoom
// Backing render resolution (px). Each callout tab shows only a handful of
// pages, so we rasterise them near the server's preview ceiling (7000px / 45MP)
// so dense plan text stays legible when the user zooms into the sheet.
const CALLOUT_RENDER_W = 4800
const CALLOUT_RENDER_CONCURRENCY = 2

/** Active callout view: { callout, zoom, items: [{canvas}] } */
let _calloutView = null
let _calloutRenderSeq = 0

function _showCalloutView(show) {
  if (!calloutView) return
  calloutView.classList.toggle('hidden', !show)
}

async function _renderCalloutView(callout, zoom = 1.0, calloutDocId = null, calloutManifest = null) {
  const renderSeq = ++_calloutRenderSeq
  calloutViewGrid.innerHTML = ''
  calloutViewTitle.textContent =
    `Callout: ${callout.text} \u2014 ${callout.pages.length} page(s), ${callout.count} instance(s)`
  _calloutView = { callout, zoom, tx: 24, ty: 24, items: [] }
  _updateCalloutZoomLabel()

  const byPage = {}
  for (const inst of callout.instances) {
    if (!byPage[inst.page]) byPage[inst.page] = []
    byPage[inst.page].push(inst)
  }
  const pages = [...new Set(callout.pages)].sort((a, b) => a - b)

  // Bound the grid width so pages wrap into a balanced grid rather than one
  // long row (the grid is absolutely positioned, so it has no natural width).
  const cols = Math.max(1, Math.ceil(Math.sqrt(pages.length)))
  const colW = CALLOUT_BASE_W + 16 /* card padding */ + 18 /* gap */
  calloutViewGrid.style.width = (cols * colW) + 'px'

  const jobs = []
  for (const pageIdx of pages) {
    const wrap = document.createElement('div')
    wrap.className = 'callout-page'
    const canvas = document.createElement('canvas')
    const label = document.createElement('span')
    label.className = 'callout-page-label'
    label.textContent = `Sheet ${pageIdx + 1}`
    wrap.appendChild(canvas)
    wrap.appendChild(label)
    calloutViewGrid.appendChild(wrap)
    _calloutView.items.push({ canvas })
    _paintCalloutCanvasMessage(canvas, `Loading sheet ${pageIdx + 1}...`)
    jobs.push({ canvas, pageIdx, instances: byPage[pageIdx] || [] })
  }
  _applyCalloutZoom()
  _renderCalloutPageQueue(jobs, renderSeq, calloutDocId, calloutManifest)
}

function _paintCalloutCanvasMessage(canvas, message) {
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  canvas.width = Math.round(CALLOUT_BASE_W * dpr)
  canvas.height = Math.round(220 * dpr)
  canvas.style.width = CALLOUT_BASE_W + 'px'
  canvas.style.height = '220px'
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = '#555'
  ctx.font = `${Math.round(12 * dpr)}px sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(message, canvas.width / 2, canvas.height / 2)
}

async function _renderCalloutPageQueue(jobs, renderSeq, calloutDocId, calloutManifest) {
  let next = 0
  async function worker() {
    while (renderSeq === _calloutRenderSeq) {
      const job = jobs[next++]
      if (!job) return
      await _renderCalloutPage(job.canvas, job.pageIdx, job.instances, renderSeq, calloutDocId, calloutManifest)
    }
  }
  const count = Math.min(CALLOUT_RENDER_CONCURRENCY, jobs.length)
  await Promise.all(Array.from({ length: count }, () => worker()))
}

async function _renderCalloutPage(canvas, pageIdx, instances, renderSeq, calloutDocId, calloutManifest) {
  if (!_pdf) return
  try {
    if (renderSeq !== _calloutRenderSeq) return
    let k = 1
    if (calloutManifest) {
      // Rendered mode: load from the snapshotted manifest (viewer may be detached)
      let rendered = null
      try {
        rendered = await viewer.renderRenderedPreviewAssetFromManifest(canvas, calloutManifest, pageIdx, CALLOUT_RENDER_W, 1)
      } catch {
        rendered = null
      }
      if (!rendered) {
        const docId = calloutDocId || state.getState().docId
        const fallback = docId
          ? await viewer.renderPdfUrlAsset(canvas, api.documentPdfUrl(docId), pageIdx, CALLOUT_RENDER_W, 1)
          : null
        if (!fallback) throw new Error(`Rendered preview unavailable for sheet ${pageIdx + 1}`)
        k = fallback.k
      } else {
        k = rendered.k
      }
      if (renderSeq !== _calloutRenderSeq) return
      canvas.style.width = CALLOUT_BASE_W + 'px'
      canvas.style.height = 'auto'
    } else {
      const page = await _pdf.getPage(pageIdx + 1)
      const vp1 = page.getViewport({ scale: 1 })
      const renderScale = CALLOUT_RENDER_W / vp1.width
      const vp = page.getViewport({ scale: renderScale })
      canvas.width = Math.round(vp.width)
      canvas.height = Math.round(vp.height)
      canvas.style.width = CALLOUT_BASE_W + 'px'
      canvas.style.height = 'auto'
      const ctx = canvas.getContext('2d')
      await page.render({ canvasContext: ctx, viewport: vp }).promise
      if (renderSeq !== _calloutRenderSeq) return
      k = renderScale * 72 / 150
    }

    // Highlights: bbox is px @150 DPI → canvas px via renderScale * 72/150.
    // Draw a soft, borderless ellipse over each callout instance.
    const renderScale = k * 150 / 72
    const PAD = 18 * renderScale
    const ctx = canvas.getContext('2d')
    ctx.save()
    for (const inst of instances) {
      const b = inst.bbox || [0, 0, 0, 0]
      const cx = (b[0] + b[2]) / 2 * k
      const cy = (b[1] + b[3]) / 2 * k
      const rx = (b[2] - b[0]) / 2 * k + PAD
      const ry = (b[3] - b[1]) / 2 * k + PAD
      ctx.beginPath()
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(255,225,0,0.30)'
      ctx.fill()
    }
    ctx.restore()
  } catch (e) {
    console.error('callout page render failed', pageIdx, e)
    _paintCalloutCanvasMessage(canvas, `Could not load sheet ${pageIdx + 1}`)
  }
}

function _applyCalloutZoom() {
  if (!_calloutView) return
  const { zoom, tx, ty } = _calloutView
  // translate + scale from the top-left so we control pan (tx/ty) and the
  // cursor-anchored zoom precisely, instead of scaling the whole layout.
  calloutViewGrid.style.transform = `translate(${tx}px, ${ty}px) scale(${zoom})`
}

function _updateCalloutZoomLabel() {
  if (calloutZoomLabel && _calloutView) {
    calloutZoomLabel.textContent = Math.round(_calloutView.zoom * 100) + '%'
  }
}

/** Zoom by `factor`, keeping the content point under (vx, vy) — viewport
 *  coordinates relative to the scroll container — anchored in place. */
function _calloutZoomAt(factor, vx, vy) {
  if (!_calloutView) return
  const prev = _calloutView.zoom
  const next = Math.max(0.15, Math.min(16, prev * factor))
  if (next === prev) return
  // Content coord under the pointer stays fixed: v = t + c*z  →  c = (v - t)/z
  const cx = (vx - _calloutView.tx) / prev
  const cy = (vy - _calloutView.ty) / prev
  _calloutView.zoom = next
  _calloutView.tx = vx - cx * next
  _calloutView.ty = vy - cy * next
  _applyCalloutZoom()
  _updateCalloutZoomLabel()
  const tab = _tabs.find(t => t.id === _activeTabId)
  if (tab) tab.calloutZoom = next
}

/** Zoom toward the centre of the scroll viewport (used by the +/- buttons). */
function _calloutZoomBy(factor) {
  if (!calloutViewScroll) return
  const rect = calloutViewScroll.getBoundingClientRect()
  _calloutZoomAt(factor, rect.width / 2, rect.height / 2)
}

document.getElementById('callout-zoom-in')?.addEventListener('click', () => _calloutZoomBy(1.25))
document.getElementById('callout-zoom-out')?.addEventListener('click', () => _calloutZoomBy(1 / 1.25))
document.getElementById('callout-zoom-fit')?.addEventListener('click', () => {
  if (!_calloutView) return
  _calloutView.zoom = 1.0
  _calloutView.tx = 24
  _calloutView.ty = 24
  _applyCalloutZoom()
  _updateCalloutZoomLabel()
})

// Mouse wheel zooms toward the pointer so you zoom onto the page you point at.
calloutViewScroll?.addEventListener('wheel', (e) => {
  if (!_calloutView) return
  e.preventDefault()
  const rect = calloutViewScroll.getBoundingClientRect()
  const vx = e.clientX - rect.left
  const vy = e.clientY - rect.top
  _calloutZoomAt(e.deltaY < 0 ? 1.12 : 1 / 1.12, vx, vy)
}, { passive: false })

// Click-and-drag to pan the pages around.
let _calloutDrag = null
calloutViewScroll?.addEventListener('mousedown', (e) => {
  if (!_calloutView || e.button !== 0) return
  e.preventDefault()
  _calloutDrag = { x: e.clientX, y: e.clientY, tx: _calloutView.tx, ty: _calloutView.ty }
  calloutViewScroll.classList.add('grabbing')
})
window.addEventListener('mousemove', (e) => {
  if (!_calloutDrag || !_calloutView) return
  _calloutView.tx = _calloutDrag.tx + (e.clientX - _calloutDrag.x)
  _calloutView.ty = _calloutDrag.ty + (e.clientY - _calloutDrag.y)
  _applyCalloutZoom()
})
window.addEventListener('mouseup', () => {
  if (!_calloutDrag) return
  _calloutDrag = null
  calloutViewScroll?.classList.remove('grabbing')
})

/**
 * Build the page strip for the current PDF.
 * Creates placeholder items immediately, prioritizes visible thumbnails,
 * then warms the whole rendered-mode strip for fast navigation.
 */
async function _buildPageStrip(pdf) {
  _clearPageStrip()
  if (!pdf) return

  pageStrip.classList.remove('hidden')
  const CANVAS_W = 76  // CSS display width in px; rendered at 2× for crispness

  // When a page filter is active (e.g. a callout tab), only show those pages.
  const filter = viewer.getPageFilter()
  const pageIndices = (filter && filter.length > 0)
    ? filter
    : Array.from({ length: pdf.numPages }, (_, i) => i)

  for (const i of pageIndices) {
    const item = document.createElement('div')
    item.className = 'strip-thumb-item'
    item.dataset.pageIdx = i

    const canvas = document.createElement('canvas')
    canvas.className = 'strip-thumb-canvas'

    const label = document.createElement('span')
    label.className = 'strip-thumb-label'
    label.textContent = i + 1

    item.appendChild(canvas)
    item.appendChild(label)
    pageStripList.appendChild(item)

    item.addEventListener('click', () => {
      state.update({ currentPage: i })
      viewer.goToPage(i)
      _updatePageLabel()
      _updateNavButtons()
      _loadPageData(i)
      _refreshSearchHighlightsForPage(i)
      _updateActiveStripThumb(i)
    })
  }

  // Highlight the current page
  _updateActiveStripThumb(state.getState().currentPage)

  // Lazy-render thumbnails as they scroll into view
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue
      observer.unobserve(entry.target)
      _queueStripThumbItem(pdf, entry.target, CANVAS_W, true)
    }
  }, { root: pageStripList, rootMargin: '300px' })

  for (const item of pageStripList.querySelectorAll('.strip-thumb-item')) {
    observer.observe(item)
  }
  pageStripList._observer = observer

  if (viewer.isRenderedMode()) {
    const generation = _thumbQueueGeneration
    setTimeout(() => {
      if (generation !== _thumbQueueGeneration) return
      for (const item of pageStripList.querySelectorAll('.strip-thumb-item')) {
        _queueStripThumbItem(pdf, item, CANVAS_W, false)
      }
    }, 150)
  }
}

function _queueStripThumbItem(pdf, item, canvasW, priority = false) {
  if (!item || item._thumbQueued || item._thumbRendered) return
  const idx = parseInt(item.dataset.pageIdx)
  if (isNaN(idx)) return
  const canvas = item.querySelector('.strip-thumb-canvas')
  if (!canvas) return

  item._thumbQueued = true
  _queueThumbRender(async () => {
    try {
      await _renderStripThumb(pdf, canvas, idx, canvasW)
      item._thumbRendered = true
    } finally {
      item._thumbQueued = false
    }
  }, priority)
}

async function _renderStripThumb(pdf, canvas, pageIdx, canvasW) {
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  await _renderOneThumb(canvas, pageIdx, canvasW, dpr)
}

function _updateActiveStripThumb(pageIdx) {
  if (!pageStripList) return
  pageStripList.querySelectorAll('.strip-thumb-item').forEach(el => el.classList.remove('active'))
  const active = pageStripList.querySelector(`.strip-thumb-item[data-page-idx="${pageIdx}"]`)
  if (active) {
    active.classList.add('active')
    active.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }
}

function _clearPageStrip() {
  _clearThumbPdf()   // destroy thumbnail worker pool on new file load
  if (pageStripList?._observer) {
    pageStripList._observer.disconnect()
    pageStripList._observer = null
  }
  if (pageStripList) pageStripList.innerHTML = ''
  pageStrip?.classList.add('hidden')
}

// ── Initial state ─────────────────────────────────────────────────────────

_createWelcomeTab()
_updatePageLabel()
_updateNavButtons()
log('Plan Viewer ready')
